/* ═══════════════════════════════════════════════════════════════
   Spec Master Editor — Draft-based editing with git-like workflow
   Word-processor style canvas with document map, undo/redo, autosave

   Flow: checkout → edit draft → save → publish/submit PR
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import {
  Pencil,
  Copy as CopyIcon,
  Plus,
  ImagePlus,
  CornerDownRight,
  GitBranch,
  Package,
  Eye,
  EyeOff,
  Trash2,
  Check,
  ChevronRight,
  Bold,
  Italic,
  Underline,
  ChevronUp,
  ChevronDown,
  Hash,
  MessageSquare,
  Undo2,
} from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect';
import {
  SpecService,
  buildInlineDiff,
  type InlineDiffBlock,
  type DiffToken,
} from '../services/specService';
import { OrgService } from '../services/orgService';

import { reportError } from '../services/errorLog';
import { Button } from '../components/ui/button';
import { HistoryButton } from '../components/EditorComponents';
import { sendPresenceJoin, sendPresenceLeave, onPresenceUpdate } from '../services/liveSync';
import type { PresenceEntry } from '../services/liveSync';
import type { SpecMasterDraft, Variant, Organization, SmartBlock } from '../../shared/types';
import { hasPermission } from '../../shared/permissions';
import { SmartBlockService } from '../services/smartBlockService';
import { ZoomCluster } from '../components/TokenComponents';
import { SmartTokenChip } from '../components/smart-blocks/SmartTokenChip';
import { SmartTokenPicker } from '../components/smart-blocks/SmartTokenPicker';
import { SmartTokenParamsPanel } from '../components/smart-blocks/SmartTokenParamsPanel';
import { BUILT_IN_SMART_BLOCKS } from '../../engine/smart-blocks';

interface BlockEditorProps {
  masterId: string;
  onBack: () => void;
  toast: (msg: string, isError?: boolean) => void;
  userRole?: 'site_admin' | 'admin' | 'manager' | 'member';
  /** The authenticated user's permission strings. All authz gates in this
   *  editor check against these (never against `userRole` directly). */
  userPermissions?: string[];
  userName?: string;
  onSavedAs?: (newId: string) => void;
  parentDirtyRef?: React.MutableRefObject<boolean>;
  exitGuardTriggerRef?: React.MutableRefObject<{ show: () => void } | null>;
  onCancelExit?: () => void;
  /** Navigate to the Smart Blocks library/Creator page. */
  onOpenSmartBlocksLibrary?: () => void;
}

/* ─── View options (localStorage-persisted, per-user editor preferences) ───
   Kept small and purely visual — none of these fields affect saved data, only
   what the user sees while editing. `trackChanges` maps to the existing diff
   mode so the "View" menu is the single home for that toggle too. ─── */
interface ViewOpts {
  /** Show blocks flagged with `excluded: true` (globally excluded from export).
   *  When off, they're hidden entirely; when on, they render dimmed. */
  showExcluded: boolean;
  /** While viewing a variant, show core blocks that this variant has in its
   *  `excludedFromVariants` list. On ⇒ show with strikethrough + EXCLUDED
   *  badge so you can see what's filtered. Off ⇒ hide them entirely, same
   *  as the compiled output. */
  showExcludedFromVariant: boolean;
  /** In the "All" meta-view, show variant blocks from every variant. On by
   *  default — turning off limits "All" to core + modules. */
  showAllVariantBlocks: boolean;
  /** While viewing a variant, dim (0.45 opacity) core blocks so variant
   *  content visually dominates. Off ⇒ core renders at full opacity — useful
   *  when you want to review the variant as its own complete document. */
  dimCoreInVariant: boolean;
  /** While viewing a variant, hide core blocks entirely. Off ⇒ core is
   *  visible (dimmed or not, per dimCoreInVariant). Turning this on gives
   *  you a variant-only view of just the variant's extra blocks. */
  hideCoreInVariant: boolean;
  /** Render the little "V1" / "Core" pills in the block gutter. Purely
   *  decorative; helps users scan variant membership at a glance. */
  showVariantPills: boolean;
  /** Render paragraph numbers (1.01, A, i, etc) to the left of each block. */
  showParagraphNumbers: boolean;
  /** Track-changes view — mirrors the top-level `diffMode` state. Keeping it
   *  in view opts gives the user one home for all visibility toggles. */
  trackChanges: boolean;
}

const DEFAULT_VIEW_OPTS: ViewOpts = {
  showExcluded: true,
  showExcludedFromVariant: true,
  showAllVariantBlocks: true,
  dimCoreInVariant: true,
  hideCoreInVariant: false,
  showVariantPills: true,
  showParagraphNumbers: true,
  trackChanges: false,
};

function loadViewOpts(): ViewOpts {
  try {
    const raw = localStorage.getItem('specforma-editor-viewOpts');
    if (!raw) return DEFAULT_VIEW_OPTS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_VIEW_OPTS, ...parsed };
  } catch {
    return DEFAULT_VIEW_OPTS;
  }
}

/* ─── Read-repair: old ContentBlock rows had `variantIds: string[]` because
   variant blocks once supported multi-variant membership. The data model now
   enforces one-variant-per-block (`variantId: string`). On every editor load
   we coerce legacy payloads in-place: take the first id from the array and
   drop the array. Writes no longer emit `variantIds`, so this migration only
   has to run once per draft/project. ─── */
function normalizeVariantBlocks(rows: any[]): any[] {
  return rows.map((b) => {
    if (!b.isVariant) return b;
    if (b.variantId) {
      const { variantIds: _drop, ...rest } = b;
      return rest;
    }
    const first = Array.isArray(b.variantIds) ? b.variantIds[0] : undefined;
    const { variantIds: _drop, ...rest } = b;
    return first ? { ...rest, variantId: first } : { ...rest, isVariant: false };
  });
}

/* ─── Undo/Redo history manager ─── */
const MAX_HISTORY = 80;
interface Snapshot {
  blocks: any[];
  systems: any[];
}
function cloneSnap(s: Snapshot): Snapshot {
  return {
    blocks: JSON.parse(JSON.stringify(s.blocks)),
    systems: JSON.parse(JSON.stringify(s.systems)),
  };
}

/** Image block with resize, caption, and graceful fallback for non-web formats */
function ImageBlock({
  imageData,
  text,
  width,
  onResize,
  isEditing,
}: {
  imageData: string;
  text?: string;
  width?: number;
  onResize?: (width: number) => void;
  isEditing?: boolean;
}) {
  const [failed, setFailed] = React.useState(false);
  const imgRef = React.useRef<HTMLDivElement>(null);
  const cleanText = (text || '')
    .replace(/&#x[0-9A-Fa-f]+;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
  const src = imageData.startsWith('data:') ? imageData : `data:image/png;base64,${imageData}`;
  const displayWidth = width ? `${width}%` : '100%';

  if (failed) {
    return (
      <div
        style={{
          margin: '4px 0',
          padding: '20px 24px',
          borderRadius: 8,
          border: '2px dashed var(--bd)',
          background: 'var(--sa)',
          textAlign: 'center',
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--tm)"
          strokeWidth="1.5"
          style={{ opacity: 0.3 }}
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginTop: 6 }}>
          Image (non-web format — EMF/WMF)
        </div>
        <div style={{ fontSize: 10, color: 'var(--tm)', opacity: 0.5, marginTop: 2 }}>
          This format will render correctly in Word export
        </div>
        {cleanText && !cleanText.startsWith('[Image:') && (
          <div style={{ fontSize: 10, color: 'var(--tm)', opacity: 0.6, marginTop: 4 }}>
            {cleanText}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={imgRef}
      style={{
        margin: '4px 0',
        position: 'relative',
        display: 'inline-block',
        maxWidth: '100%',
        width: displayWidth,
      }}
    >
      <img
        src={src}
        alt={cleanText || 'Image'}
        style={{ width: '100%', borderRadius: 4, border: '1px solid var(--bd)', display: 'block' }}
        onError={() => setFailed(true)}
      />
      {/* Resize handle — bottom-right corner */}
      {isEditing && onResize && (
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const container = imgRef.current?.parentElement;
            if (!container) return;
            const containerW = container.offsetWidth;
            const startPct = width || 100;
            function onMove(ev: MouseEvent) {
              const deltaPx = ev.clientX - startX;
              const deltaPct = (deltaPx / containerW) * 100;
              const newPct = Math.max(20, Math.min(100, startPct + deltaPct));
              onResize!(Math.round(newPct));
            }
            function onUp() {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
              document.body.style.cursor = '';
              document.body.style.userSelect = '';
            }
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
          }}
          style={{
            position: 'absolute',
            bottom: 2,
            right: -4,
            width: 10,
            height: 10,
            cursor: 'ew-resize',
            borderRadius: 2,
            background: 'var(--ac)',
            opacity: 0.6,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.6';
          }}
        />
      )}
      {/* Width indicator */}
      {isEditing && width && width < 100 && (
        <div
          style={{
            position: 'absolute',
            bottom: -14,
            right: 0,
            fontSize: 10,
            color: 'var(--tm)',
            fontFamily: "'JetBrains Mono'",
          }}
        >
          {width}%
        </div>
      )}
      {cleanText && !cleanText.startsWith('[Image:') && (
        <div style={{ fontSize: 10, color: 'var(--tm)', marginTop: 4, fontStyle: 'italic' }}>
          {cleanText}
        </div>
      )}
    </div>
  );
}

function BlockEditor({
  masterId,
  onBack,
  toast,
  userRole = 'admin',
  userPermissions = [],
  userName = 'user',
  onSavedAs,
  parentDirtyRef,
  exitGuardTriggerRef,
  onCancelExit,
  onOpenSmartBlocksLibrary,
}: BlockEditorProps) {
  const [draft, setDraft] = React.useState<SpecMasterDraft | null>(null);
  const [master, setMaster] = React.useState<any>(null);
  const [blocks, setBlocks] = React.useState<any[]>([]);
  const [systems, setSystems] = React.useState<any[]>([]);
  const [variants, setVariants] = React.useState<Variant[]>([]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  React.useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);
  const [lastClickedId, setLastClickedId] = React.useState<string | null>(null);
  const [dirty, setDirty] = React.useState(false);
  // Sync dirty state to parent ref for sidebar exit guard.
  React.useEffect(() => {
    if (parentDirtyRef) parentDirtyRef.current = dirty;
  }, [dirty, parentDirtyRef]);
  // Expose exit guard trigger so sidebar nav can reuse this modal
  React.useEffect(() => {
    if (exitGuardTriggerRef) exitGuardTriggerRef.current = { show: () => setShowExitGuard(true) };
    return () => {
      if (exitGuardTriggerRef) exitGuardTriggerRef.current = null;
    };
  }, [exitGuardTriggerRef]);
  const [editingTitle, setEditingTitle] = React.useState(false);
  const [editingCsi, setEditingCsi] = React.useState(false);
  const [editingBlockId, setEditingBlockId] = React.useState<string | null>(null);
  const [zoom, setZoom] = React.useState(() => {
    const saved = localStorage.getItem('specforma-editor-zoom');
    return saved ? Math.max(0.3, Math.min(2, +saved)) : 1;
  });
  // Canvas column — positioning parent for the floating zoom cluster and the
  // measurement basis for fit-to-window (page is a fixed 680px sheet).
  const canvasColRef = React.useRef<HTMLDivElement>(null);
  const fitZoomToWindow = React.useCallback(() => {
    const col = canvasColRef.current;
    if (!col) return;
    const avail = col.clientWidth - 40; // canvas horizontal padding
    setZoom(Math.max(0.3, Math.min(2, +(avail / 680).toFixed(2))));
  }, []);
  const [showSaveAsModal, setShowSaveAsModal] = React.useState(false);
  const [showVersionHistory, setShowVersionHistory] = React.useState(false);
  const [showPublishModal, setShowPublishModal] = React.useState(false);
  // Inline track-changes-style diff view that replaces the editor canvas when on.
  const [diffMode, setDiffMode] = React.useState(false);
  const [inlineDiffData, setInlineDiffData] = React.useState<InlineDiffBlock[]>([]);
  // View options — persisted per-user in localStorage. Consumed by rendering
  // filters below so the user can reshape what's visible without touching data.
  const [viewOpts, setViewOpts] = React.useState<ViewOpts>(() => loadViewOpts());
  React.useEffect(() => {
    localStorage.setItem('specforma-editor-viewOpts', JSON.stringify(viewOpts));
  }, [viewOpts]);
  const [showViewMenu, setShowViewMenu] = React.useState(false);
  const viewMenuRef = React.useRef<HTMLDivElement>(null);
  // Smart Blocks — firm-published catalog ONLY. Built-ins are merged in at
  // the picker / chip / params layer via findSmartBlockInCatalog's fallback.
  // (Storing them here would double them up in the picker, since the picker
  // also prepends BUILT_IN_SMART_BLOCKS.)
  const [smartBlockCatalog, setSmartBlockCatalog] = React.useState<SmartBlock[]>([]);
  const refreshSmartBlockCatalog = React.useCallback(async () => {
    try {
      const firmList = await SmartBlockService.list();
      setSmartBlockCatalog(firmList);
    } catch {
      setSmartBlockCatalog([]);
    }
  }, []);
  React.useEffect(() => {
    void refreshSmartBlockCatalog();
    const onSync = (ev: any) => {
      const tables: string[] = ev?.detail?.tables ?? [];
      if (tables.includes('smart_blocks')) void refreshSmartBlockCatalog();
    };
    window.addEventListener('specforma-sync-complete', onSync);
    return () => window.removeEventListener('specforma-sync-complete', onSync);
  }, [refreshSmartBlockCatalog]);

  const [showSmartTokenPicker, setShowSmartTokenPicker] = React.useState(false);
  // Where the picker should insert: an existing block id to insert after,
  // OR -1 to append, OR null when the picker isn't open.
  const smartTokenInsertAnchorRef = React.useRef<string | -1 | null>(null);
  const [showExitGuard, setShowExitGuard] = React.useState(false);
  const [rightPanelWidth, setRightPanelWidth] = React.useState(() => {
    const saved = localStorage.getItem('specforma-editor-rightPanel');
    return saved ? Math.max(200, Math.min(600, +saved)) : 300;
  });
  const [rightTab, setRightTab] = React.useState<'block' | 'smart-blocks'>('block');
  const [showTablePicker, setShowTablePicker] = React.useState(false);
  const [tablePickerSize, setTablePickerSize] = React.useState<{ r: number; c: number }>({
    r: 0,
    c: 0,
  });
  const [saveAsTitle, setSaveAsTitle] = React.useState('');
  const [publishMessage, setPublishMessage] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [dragOverIdx, setDragOverIdx] = React.useState<number | null>(null);
  const [dragFromIdx, setDragFromIdx] = React.useState<number | null>(null);
  const dragDataRef = React.useRef<string | null>(null); // fallback for dataTransfer
  // ─── Custom block drag (mouse-event based, more reliable than HTML5 drag in Electron) ───
  const [blockDragIdx, setBlockDragIdx] = React.useState<number | null>(null);
  const [blockDragPos, setBlockDragPos] = React.useState<{ x: number; y: number } | null>(null);
  const [blockDropIdx, setBlockDropIdx] = React.useState<number | null>(null);
  const blockDragRef = React.useRef<number | null>(null);
  // Mirror of selectedIds for the drag effect (it deliberately re-binds only
  // on blockDragIdx, so it reads the selection through a ref).
  const selectedIdsRef = React.useRef<Set<string>>(new Set());
  const blockDropRef = React.useRef<number | null>(null);
  const [contextMenu, setContextMenu] = React.useState<{
    x: number;
    y: number;
    blockId: string;
    idx: number;
  } | null>(null);
  const [moduleSearch, setModuleSearch] = React.useState('');
  const [colorPickerGroupId, setColorPickerGroupId] = React.useState<string | null>(null);
  const [moduleManageMode, setModuleManageMode] = React.useState(false);
  const [moduleSelectedIds, setModuleSelectedIds] = React.useState<Set<string>>(new Set());
  const [moduleSortBy, setModuleSortBy] = React.useState<'name' | 'blocks' | 'created'>('created');
  const [organizations, setOrganizations] = React.useState<Organization[]>([]);

  // ─── Presence state ───
  const [presence, setPresence] = React.useState<PresenceEntry[]>([]);

  // ─── Document map search ───
  const [docMapSearch, setDocMapSearch] = React.useState('');

  // ─── Find & Replace ───
  const [showFind, setShowFind] = React.useState(false);
  const [showReplace, setShowReplace] = React.useState(false);
  const [findText, setFindText] = React.useState('');
  const [replaceText, setReplaceText] = React.useState('');
  const [findMatchCase, setFindMatchCase] = React.useState(false);
  const [findMatchIdx, setFindMatchIdx] = React.useState(0);
  const findInputRef = React.useRef<HTMLInputElement>(null);

  // ─── Refs for stable access from effects/handlers ───
  const draftRef = React.useRef(draft);
  const blocksRef = React.useRef(blocks);
  const systemsRef = React.useRef(systems);
  const variantsRef = React.useRef(variants);
  draftRef.current = draft;
  blocksRef.current = blocks;
  systemsRef.current = systems;
  variantsRef.current = variants;

  // ─── Custom block drag effect ───
  React.useEffect(() => {
    if (blockDragIdx === null) return;
    function onMove(e: MouseEvent) {
      setBlockDragPos({ x: e.clientX, y: e.clientY });
      // Native hit-test. `elementFromPoint` and `MouseEvent.clientY` share the
      // same coord system at every zoom factor, so no rect-vs-client math is
      // needed. Replaces the manual `/ zoomFactor` correction from v2.5.43
      // that didn't fully fix the offset.
      const hit = document.elementFromPoint(e.clientX, e.clientY);
      const blockEl = hit && ((hit as Element).closest('[data-block-idx]') as HTMLElement | null);
      if (!blockEl) return;
      const idx = parseInt(blockEl.dataset.blockIdx || '0');
      const rect = blockEl.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const dropAt = e.clientY < midY ? idx : idx + 1;
      blockDropRef.current = dropAt;
      setBlockDropIdx(dropAt);
    }
    function onUp() {
      const dropAt = blockDropRef.current;
      const fromIdx = blockDragRef.current;
      if (fromIdx !== null && dropAt !== null && fromIdx !== dropAt && fromIdx !== dropAt - 1) {
        const currentBlocks = [...blocksRef.current];
        const dragged = currentBlocks[fromIdx];
        const sel = selectedIdsRef.current;
        if (dragged && sel.size > 1 && sel.has(dragged.id)) {
          // Multi-drag: the whole selection moves as one contiguous run,
          // document order preserved.
          const moving = currentBlocks.filter((b: any) => sel.has(b.id));
          const removedBeforeDrop = currentBlocks
            .slice(0, dropAt)
            .filter((b: any) => sel.has(b.id)).length;
          const remaining = currentBlocks.filter((b: any) => !sel.has(b.id));
          const insertAt = Math.max(0, Math.min(remaining.length, dropAt - removedBeforeDrop));
          remaining.splice(insertAt, 0, ...moving);
          setBlocks(remaining);
          setDirty(true);
          setSelectedId(dragged.id);
        } else {
          const [moved] = currentBlocks.splice(fromIdx, 1);
          const insertAt = dropAt > fromIdx ? dropAt - 1 : dropAt;
          currentBlocks.splice(insertAt, 0, moved);
          setBlocks(currentBlocks);
          setDirty(true);
          setSelectedId(moved.id);
        }
      }
      setBlockDragIdx(null);
      setBlockDragPos(null);
      setBlockDropIdx(null);
      blockDragRef.current = null;
      blockDropRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [blockDragIdx]);

  const [showLevelPicker, setShowLevelPicker] = React.useState<{
    idx: number;
    fromIdx: number;
  } | null>(null);
  // Authz — permission-first. `userRole` is retained only for legacy
  // display labels; every gate reads granular permission keys from the
  // auth'd user's permissions array.
  const canDirectPublish = hasPermission(userPermissions, 'specs.publish');
  const canDelete = hasPermission(userPermissions, 'specs.delete');
  const canOverrideDraft = hasPermission(userPermissions, 'specs.override_draft');

  // ─── Edit mode: 'block' = select/drag/group, 'text' = click-to-edit ───
  // One-mode editing (2026-07): the Select/Write toggle is gone. Click text to
  // type; the gutter handle selects/drags; Esc bridges typing → selection.
  const [activeVariantId, setActiveVariantId] = React.useState<string | null>(null);

  // ─── Presence effects (need activeVariantId declared first) ───
  React.useEffect(() => {
    sendPresenceJoin(masterId, activeVariantId, userName);
    return () => {
      sendPresenceLeave();
    };
  }, [masterId]);
  React.useEffect(() => {
    sendPresenceJoin(masterId, activeVariantId, userName);
  }, [activeVariantId]);
  React.useEffect(() => {
    const unsub = onPresenceUpdate((entries) => {
      const others = entries.filter((e) => e.specId === masterId && e.userName !== userName);
      setPresence(others);
    });
    return unsub;
  }, [masterId, userName]);

  // ─── Document map panel ───
  const [docMapWidth, setDocMapWidth] = React.useState(() => {
    const saved = localStorage.getItem('specforma-editor-docMap');
    return saved ? Math.max(140, Math.min(400, +saved)) : 220;
  });
  // Persist panel widths + zoom to localStorage
  React.useEffect(() => {
    localStorage.setItem('specforma-editor-zoom', String(zoom));
  }, [zoom]);
  React.useEffect(() => {
    localStorage.setItem('specforma-editor-rightPanel', String(rightPanelWidth));
  }, [rightPanelWidth]);
  React.useEffect(() => {
    localStorage.setItem('specforma-editor-docMap', String(docMapWidth));
  }, [docMapWidth]);

  const [collapsedMapIds, setCollapsedMapIds] = React.useState<Set<string>>(new Set());
  const docMapInitRef = React.useRef(false);
  const canvasRef = React.useRef<HTMLDivElement>(null);

  // ─── Undo/Redo stack ───
  const historyRef = React.useRef<Snapshot[]>([]);
  const historyIdxRef = React.useRef(-1);
  const skipHistoryRef = React.useRef(false);

  // ─── Auto-collapse L3+ headings in doc map on first load ───
  React.useEffect(() => {
    if (docMapInitRef.current || blocks.length === 0) return;
    docMapInitRef.current = true;
    const numbered = buildNumberedBlocks(blocks);
    const mapItems = numbered.filter((b: any) => b.level <= 4);
    const toCollapse = new Set<string>();
    mapItems.forEach((b: any, i: number) => {
      if (b.level >= 3 && i < mapItems.length - 1 && mapItems[i + 1].level > b.level) {
        toCollapse.add(b.id);
      }
    });
    if (toCollapse.size > 0) setCollapsedMapIds(toCollapse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks.length]);

  // ─── Lazy draft checkout ──────────────────────────────────────
  // Defer draft creation (and, for spec masters, the lock) until the
  // user actually makes a change. Watching `dirty` flip false→true is
  // the single trigger; if a draft already exists (resumed at mount or
  // just created), we no-op. This is the "no edit → no draft" UX fix
  // that stops dropping stray drafts on view-and-leave.
  const lazyCheckoutInFlight = React.useRef(false);
  React.useEffect(() => {
    if (!dirty) return;
    if (lazyCheckoutInFlight.current) return;
    if (draft) return;
    lazyCheckoutInFlight.current = true;
    (async () => {
      try {
        if (master) {
          const fresh = await SpecService.checkoutBlueprint(masterId, userName);
          if (fresh) {
            const merged: SpecMasterDraft = {
              ...fresh,
              coreContent: blocksRef.current,
              systems: systemsRef.current,
              variants: variantsRef.current,
            };
            const saved = await SpecService.saveDraft(merged);
            setDraft(saved);
          }
        }
      } catch (err: any) {
        // Lock collision (someone else holds the master) is the most
        // common reason. Surface the message to the user and revert
        // dirty so the exit guard doesn't fire on imaginary edits.
        toast(err?.message || 'Could not start editing', true);
        setDirty(false);
      } finally {
        lazyCheckoutInFlight.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  // ─── Autosave (30s interval) ───
  const autoSaveRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const dirtyRef = React.useRef(dirty);
  dirtyRef.current = dirty;

  React.useEffect(() => {
    // Masters keep the 30s draft autosave.
    autoSaveRef.current = setInterval(() => {
      if (!dirtyRef.current) return;
      if (draftRef.current) {
        const d = draftRef.current;
        const updated: SpecMasterDraft = {
          ...d,
          coreContent: blocksRef.current,
          systems: systemsRef.current,
          variants: variantsRef.current,
        };
        SpecService.saveDraft(updated).then((saved) => {
          draftRef.current = saved;
        });
      }
    }, 30000);
    return () => {
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    };
  }, []);

  // ─── Push undo snapshot on blocks/systems change ───
  React.useEffect(() => {
    if (skipHistoryRef.current) {
      skipHistoryRef.current = false;
      return;
    }
    if (!blocks.length && !systems.length) return;
    const snap = cloneSnap({ blocks, systems });
    const h = historyRef.current;
    const idx = historyIdxRef.current;
    if (idx < h.length - 1) h.splice(idx + 1);
    h.push(snap);
    if (h.length > MAX_HISTORY) h.shift();
    historyIdxRef.current = h.length - 1;
  }, [blocks, systems]);

  function undo() {
    const h = historyRef.current;
    const idx = historyIdxRef.current;
    if (idx <= 0) return;
    const prev = cloneSnap(h[idx - 1]);
    historyIdxRef.current = idx - 1;
    skipHistoryRef.current = true;
    setBlocks(prev.blocks);
    setSystems(prev.systems);
    setDirty(true);
  }

  function redo() {
    const h = historyRef.current;
    const idx = historyIdxRef.current;
    if (idx >= h.length - 1) return;
    const next = cloneSnap(h[idx + 1]);
    historyIdxRef.current = idx + 1;
    skipHistoryRef.current = true;
    setBlocks(next.blocks);
    setSystems(next.systems);
    setDirty(true);
  }

  React.useEffect(() => {
    initEditor();
  }, [masterId]);

  // ─── Keyboard handler ───
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tgt = e.target as HTMLElement;
      const inInput =
        tgt &&
        (tgt.tagName === 'INPUT' ||
          tgt.tagName === 'TEXTAREA' ||
          tgt.tagName === 'SELECT' ||
          tgt.isContentEditable);

      // Find & Replace — works from any context
      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault();
        setShowFind(true);
        setShowReplace(false);
        setTimeout(() => findInputRef.current?.focus(), 50);
        return;
      }
      if (e.ctrlKey && e.key === 'h') {
        e.preventDefault();
        setShowFind(true);
        setShowReplace(true);
        setTimeout(() => findInputRef.current?.focus(), 50);
        return;
      }
      if (e.key === 'Escape' && showFind) {
        setShowFind(false);
        setShowReplace(false);
        setFindText('');
        return;
      }

      if (inInput) {
        if (e.ctrlKey && e.key === 's') {
          e.preventDefault();
          doSave();
        } else if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          undo();
        } else if ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'Z')) {
          e.preventDefault();
          redo();
        }
        return;
      }

      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        doSave();
        return;
      }
      if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'Z')) {
        e.preventDefault();
        redo();
        return;
      }

      // Block clipboard — real structured copy/cut/paste (2026-07). Text-level
      // clipboard inside the textarea is untouched (inInput returns above).
      if (e.ctrlKey && e.key === 'c' && selectedId) {
        e.preventDefault();
        copyBlocksToClipboard(false);
        return;
      }
      if (e.ctrlKey && e.key === 'x' && selectedId) {
        e.preventDefault();
        copyBlocksToClipboard(true);
        return;
      }
      // Paste — SpecForma block payload wins; anything else falls through to
      // the external text/table paste (Word/Excel).
      if (e.ctrlKey && e.key === 'v') {
        e.preventDefault();
        pasteBlocksFromClipboard();
        return;
      }

      if (!selectedId) return;
      const currentBlocks = getCurrentBlocks();
      const idx = currentBlocks.findIndex((b: any) => b.id === selectedId);
      if (idx < 0) return;
      if (e.key === 'ArrowUp' && idx > 0) {
        e.preventDefault();
        setSelectedId(currentBlocks[idx - 1].id);
      }
      if (e.key === 'ArrowDown' && idx < currentBlocks.length - 1) {
        e.preventDefault();
        setSelectedId(currentBlocks[idx + 1].id);
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        const delta = e.shiftKey ? -1 : 1;
        if (selectedIds.size > 1) changeLevelForIds([...selectedIds], delta);
        else changeLevel(idx, delta);
      }
      if (e.key === 'Enter' && !e.ctrlKey) {
        // Enter on a selected block drops into its text (Esc gets back out).
        // New-block-below lives on Enter inside the text editor (split at end).
        e.preventDefault();
        setEditingBlockId(selectedId);
      }
      if (e.ctrlKey && e.key === 'Enter') {
        // Ctrl+Enter — insert a fresh block below without entering the text.
        e.preventDefault();
        addBlockAfter(idx);
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && !editingBlockId) {
        e.preventDefault();
        // Multi-select delete
        const idsToRemove = selectedIds.size > 0 ? selectedIds : new Set([selectedId!]);
        // If viewing a variant, Delete on a module (multi-variant) unassigns
        // it from this variant. Block-level variants are single-variant by
        // design so Delete just deletes them (they can't live anywhere else).
        const isViewingVariant = activeVariantId && activeVariantId !== 'core';
        if (isViewingVariant) {
          const idsToHardDelete: string[] = [];
          idsToRemove.forEach((bid) => {
            const b = blocksRef.current.find((bl: any) => bl.id === bid);
            if (!b?.isVariant) return;
            idsToHardDelete.push(bid);
          });
          if (idsToHardDelete.length > 0 && canDelete) {
            setBlocks((prev) => prev.filter((bl: any) => !idsToHardDelete.includes(bl.id)));
          }
          setSelectedIds(new Set());
          setSelectedId(null);
          setDirty(true);
        } else if (canDelete) {
          // Actually delete blocks
          const bs = getCurrentBlocks();
          const remaining = bs.filter((b: any) => !idsToRemove.has(b.id));
          if (remaining.length > 0) {
            setCurrentBlocks(remaining);
            setSelectedId(remaining[0]?.id || null);
            setSelectedIds(new Set());
          }
        }
      }
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        if (selectedIds.size > 1) duplicateBlocks([...selectedIds]);
        else duplicateBlock(idx);
      }
      if (e.key === 'Escape') {
        setSelectedId(null);
        setEditingBlockId(null);
        setShowLevelPicker(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, selectedIds, blocks, systems, dirty, editingBlockId, activeVariantId, showFind]);

  // ─── Block clipboard (structured copy/cut/paste, 2026-07) ───
  // Payload rides the OS clipboard as JSON (works across sections and across
  // masters/projects in any window); an in-memory fallback covers clipboard-
  // read denials. Variant linkage survives only within the same document —
  // pasting into another master/project lands the blocks as core.
  const blockClipboardRef = React.useRef<any | null>(null);
  const clipboardDocId = () => masterId || '';

  function copyBlocksToClipboard(cut: boolean) {
    const currentBlocks = getCurrentBlocks();
    const ids = selectedIds.size > 0 ? selectedIds : selectedId ? new Set([selectedId]) : new Set();
    const moving = currentBlocks.filter((b: any) => ids.has(b.id));
    if (!moving.length) return;
    if (cut && !canDelete) {
      toast('You need delete permission to cut blocks');
      return;
    }
    if (cut && moving.length >= currentBlocks.length) {
      toast('Cannot cut every block in the section');
      return;
    }
    const payload = {
      __specforma: 'blocks-v1',
      sourceDocId: clipboardDocId(),
      blocks: JSON.parse(JSON.stringify(moving)),
    };
    blockClipboardRef.current = payload;
    navigator.clipboard?.writeText(JSON.stringify(payload)).catch(() => {});
    if (cut) {
      const remaining = currentBlocks.filter((b: any) => !ids.has(b.id));
      setCurrentBlocks(remaining);
      setSelectedIds(new Set());
      setSelectedId(remaining[0]?.id || null);
    }
    toast(`${moving.length} block${moving.length === 1 ? '' : 's'} ${cut ? 'cut' : 'copied'}`);
  }

  async function pasteBlocksFromClipboard() {
    let payload: any = null;
    try {
      const text = await navigator.clipboard.readText();
      if (text && text.startsWith('{') && text.includes('"__specforma":"blocks-v1"')) {
        payload = JSON.parse(text);
      } else if (text) {
        // Clipboard holds non-SpecForma content — the external handler owns it.
        payload = null;
      }
    } catch {
      // Read denied/failed — fall back to the in-memory copy from this window.
      payload = blockClipboardRef.current;
    }
    if (!payload) {
      handleExternalPaste();
      return;
    }
    const sameDoc = payload.sourceDocId === clipboardDocId();
    const clones = (payload.blocks || []).map((b: any) => {
      const clone = { ...JSON.parse(JSON.stringify(b)), id: newBlockId() };
      if (!sameDoc) {
        // Variant identities don't exist outside their home document.
        delete clone.isVariant;
        delete clone.variantId;
        delete clone.excludedFromVariants;
      }
      return clone;
    });
    if (!clones.length) {
      handleExternalPaste();
      return;
    }
    const currentBlocks = getCurrentBlocks();
    const anchorIdx = selectedId
      ? currentBlocks.findIndex((b: any) => b.id === selectedId)
      : currentBlocks.length - 1;
    const newBlocks = [...currentBlocks];
    newBlocks.splice((anchorIdx < 0 ? currentBlocks.length - 1 : anchorIdx) + 1, 0, ...clones);
    setCurrentBlocks(newBlocks);
    setSelectedIds(new Set(clones.map((c: any) => c.id)));
    setSelectedId(clones[0].id);
    toast(`${clones.length} block${clones.length === 1 ? '' : 's'} pasted`);
  }

  // ─── External paste handler (text + table detection) ───
  async function handleExternalPaste() {
    try {
      // Try to read HTML first (Word/browser tables), fall back to plain text (Excel TSV)
      let html = '';
      let text = '';
      try {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          if (item.types.includes('text/html')) {
            const blob = await item.getType('text/html');
            html = await blob.text();
          }
          if (item.types.includes('text/plain')) {
            const blob = await item.getType('text/plain');
            text = await blob.text();
          }
        }
      } catch {
        text = await navigator.clipboard.readText();
      }

      if (!text && !html) return;

      const currentBlocks = getCurrentBlocks();
      const insertIdx = selectedId
        ? currentBlocks.findIndex((b: any) => b.id === selectedId)
        : currentBlocks.length - 1;
      const baseLevel = currentBlocks[insertIdx]?.level || 3;

      // Detect HTML table (from Word, browser, Google Docs)
      if (html && /<table[\s>]/i.test(html)) {
        const tableBlock = parseHtmlTable(html, baseLevel);
        if (tableBlock) {
          const updated = [...currentBlocks];
          updated.splice(insertIdx + 1, 0, tableBlock);
          setCurrentBlocks(updated);
          setSelectedId(tableBlock.id);
          setDirty(true);
          toast('Pasted table from clipboard');
          return;
        }
      }

      // Detect TSV (tab-separated values from Excel/Sheets)
      if (text && text.includes('\t') && text.includes('\n')) {
        const rows = text.split(/\r?\n/).filter((l) => l.trim());
        if (rows.length >= 2 || rows.some((r) => r.split('\t').length >= 2)) {
          const cells = rows.map((r) => r.split('\t').map((c) => ({ text: c.trim() })));
          const maxCols = Math.max(...cells.map((r) => r.length));
          // Pad rows to have equal columns
          const paddedCells = cells.map((r) => {
            while (r.length < maxCols) r.push({ text: '' });
            return r;
          });
          const colWidths = Array(maxCols).fill(Math.round((100 / maxCols) * 100) / 100);
          // Adjust last column to make sum exactly 100
          colWidths[maxCols - 1] = +(
            100 - colWidths.slice(0, -1).reduce((a: number, b: number) => a + b, 0)
          ).toFixed(2);
          const tableBlock = {
            id: 'blk-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
            level: baseLevel,
            text: '',
            blockType: 'table' as const,
            tableData: {
              rows: paddedCells.length,
              cols: maxCols,
              cells: paddedCells,
              colWidths,
              borderWidth: 1,
              borderColor: '000000',
              borderStyle: 'solid' as const,
              innerBorderWidth: 0.5,
              innerBorderColor: '999999',
              innerBorderStyle: 'solid' as const,
              headerRows: 1,
              headerBgColor: 'E8E8E8',
              headerBgOpacity: 100,
            },
          };
          const updated = [...currentBlocks];
          updated.splice(insertIdx + 1, 0, tableBlock);
          setCurrentBlocks(updated);
          setSelectedId(tableBlock.id);
          setDirty(true);
          toast(`Pasted table (${paddedCells.length} rows × ${maxCols} cols)`);
          return;
        }
      }

      // Plain text — create text blocks
      if (!text) return;
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length === 0) return;
      const newBlocks = lines.map((line) => ({
        id: 'blk-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
        level: baseLevel,
        text: line.trim(),
      }));
      const updated = [...currentBlocks];
      updated.splice(insertIdx + 1, 0, ...newBlocks);
      setCurrentBlocks(updated);
      setSelectedId(newBlocks[newBlocks.length - 1].id);
      setDirty(true);
      toast(`Pasted ${newBlocks.length} block${newBlocks.length !== 1 ? 's' : ''}`);
    } catch {
      // Clipboard API not available
    }
  }

  /** Parse an HTML table from clipboard into a table block */
  function parseHtmlTable(html: string, level: number): any | null {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const table = doc.querySelector('table');
      if (!table) return null;
      const trs = table.querySelectorAll('tr');
      if (trs.length === 0) return null;
      const cells: any[][] = [];
      let maxCols = 0;
      trs.forEach((tr) => {
        const row: any[] = [];
        tr.querySelectorAll('td, th').forEach((td) => {
          const text = (td.textContent || '').trim();
          const bold =
            td.tagName === 'TH' ||
            (td as HTMLElement).style.fontWeight === 'bold' ||
            td.querySelector('b, strong') !== null;
          row.push({ text, bold: bold || undefined });
        });
        if (row.length > maxCols) maxCols = row.length;
        cells.push(row);
      });
      if (cells.length === 0 || maxCols === 0) return null;
      // Pad rows
      cells.forEach((row) => {
        while (row.length < maxCols) row.push({ text: '' });
      });
      const colWidths = Array(maxCols).fill(Math.round((100 / maxCols) * 100) / 100);
      colWidths[maxCols - 1] = +(
        100 - colWidths.slice(0, -1).reduce((a: number, b: number) => a + b, 0)
      ).toFixed(2);
      // Detect if first row is header (all TH or bold)
      const firstRowTh = table.querySelectorAll('tr:first-child th').length > 0;
      return {
        id: 'blk-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
        level,
        text: '',
        blockType: 'table',
        tableData: {
          rows: cells.length,
          cols: maxCols,
          cells,
          colWidths,
          borderWidth: 1,
          borderColor: '000000',
          borderStyle: 'solid',
          innerBorderWidth: 0.5,
          innerBorderColor: '999999',
          innerBorderStyle: 'solid',
          headerRows: firstRowTh ? 1 : 0,
          headerBgColor: firstRowTh ? 'E8E8E8' : undefined,
          headerBgOpacity: firstRowTh ? 100 : undefined,
        },
      };
    } catch {
      return null;
    }
  }

  async function doSave() {
    const d = draftRef.current;
    if (!d) return;
    const updated: SpecMasterDraft = {
      ...d,
      coreContent: blocksRef.current,
      systems: systemsRef.current,
      variants: variantsRef.current,
    };
    const saved = await SpecService.saveDraft(updated);
    setDraft(saved);
    setDirty(false);
    toast('Draft saved');
  }

  async function initEditor() {
    setLoading(true);
    setError(null);
    try {
      // Spec master mode. Load the published master and resume an
      // existing draft if there is one. If not, render baseline content
      // and DEFER both the draft creation AND the lock until the first
      // edit. This is the change that fixes "open + back out leaves a
      // lock". No edit → no draft → no lock.
      const m = await SpecService.getMaster(masterId);
      if (!m) {
        setError('Spec Master not found');
        setLoading(false);
        return;
      }
      setMaster(m);
      const existingDraft = await SpecService.getDraftForBlueprint(m.id, userName);
      if (existingDraft) {
        setDraft(existingDraft);
        setBlocks(normalizeVariantBlocks(existingDraft.coreContent || []));
        setSystems(existingDraft.systems || []);
        setVariants(existingDraft.variants || []);
        setDirty(true);
      } else {
        setDraft(null);
        setBlocks(normalizeVariantBlocks(m.coreContent || []));
        setSystems(m.systems || []);
        setVariants(m.variants || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load spec master');
    }
    // Load organizations for variant-client association
    try {
      setOrganizations(await OrgService.list());
    } catch (err) {
      reportError('BlockEditor:loadOrgs', err);
    }
    setLoading(false);
  }

  async function handleSave() {
    try {
      if (!draft) return;
      const updated: SpecMasterDraft = {
        ...draft,
        coreContent: blocks,
        systems,
        variants,
      };
      const saved = await SpecService.saveDraft(updated);
      setDraft(saved);
      setDirty(false);
      toast('Draft saved');
    } catch (err: any) {
      console.error('[BlockEditor] Save failed:', err);
      toast('Save failed: ' + (err.message || 'Unknown error'), true);
    }
  }

  async function handlePublish() {
    if (!draft) return;
    try {
      const updated: SpecMasterDraft = {
        ...draft,
        coreContent: blocks,
        systems,
        variants,
      };
      const saved = await SpecService.saveDraft(updated);
      if (canDirectPublish) {
        const result = await SpecService.directPublish(saved, userName);
        if (result) {
          toast(`Published v${result.version}`);
          onBack();
        }
      } else {
        setShowPublishModal(true);
      }
    } catch (err: any) {
      console.error('[BlockEditor] Publish failed:', err);
      toast('Publish failed: ' + (err.message || 'Unknown error'), true);
    }
  }

  async function handleSubmitForReview() {
    if (!draft) return;
    try {
      const updated: SpecMasterDraft = {
        ...draft,
        coreContent: blocks,
        systems,
        variants,
      };
      await SpecService.saveDraft(updated);
      const result = await SpecService.submitPublishRequest(draft.id, publishMessage);
      if (result) {
        setDraft(result);
        setDirty(false);
        toast('Publish request submitted for review');
      }
      setShowPublishModal(false);
      setPublishMessage('');
    } catch (err: any) {
      console.error('[BlockEditor] Submit for review failed:', err);
      toast('Submit failed: ' + (err.message || 'Unknown error'), true);
    }
  }

  async function handleSaveAs() {
    if (!master || !saveAsTitle.trim()) return;
    try {
      const saved = await SpecService.saveAsNewMaster(
        { ...master, coreContent: blocks, systems, variants },
        saveAsTitle.trim(),
        userName
      );
      setShowSaveAsModal(false);
      setSaveAsTitle('');
      toast(`Saved as "${saved.title}" — v1`);
      if (onSavedAs) onSavedAs(saved.id);
    } catch (err: any) {
      console.error('[BlockEditor] Save As failed:', err);
      toast('Save As failed: ' + (err.message || 'Unknown error'), true);
    }
  }

  async function handleDiscard() {
    if (!draft) return;
    if (!confirm('Discard all changes and unlock this spec master? This cannot be undone.')) return;
    try {
      await SpecService.discardDraft(draft.id);
      toast('Draft discarded');
      onBack();
    } catch (err: any) {
      console.error('[BlockEditor] Discard failed:', err);
      toast('Discard failed: ' + (err.message || 'Unknown error'), true);
    }
  }

  async function handleShowDiff() {
    // Track-changes-style inline diff — replaces the editor canvas with a
    // read-only view colored per block (added / removed / modified / unchanged)
    // with word-level highlighting on modifications.
    if (!master) return;
    const published = master.coreContent || [];
    setInlineDiffData(buildInlineDiff(published, blocks));
    setDiffMode((prev) => !prev);
  }

  async function handleRevert(targetVersion: number) {
    if (!master) return;
    if (!confirm(`Revert published spec master to version ${targetVersion}?`)) return;
    const result = await SpecService.revertMaster(master.id, targetVersion, userName);
    if (result) {
      setMaster(result);
      toast(`Reverted to v${targetVersion} → now v${result.version}`);
    }
    setShowVersionHistory(false);
  }

  function getCurrentBlocks() {
    return blocks;
  }

  function setCurrentBlocks(newBlocks: any[]) {
    setBlocks(newBlocks);
    setDirty(true);
  }

  function updateBlock(id: string, updates: any) {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...updates } : b)));
    setDirty(true);
  }

  function changeLevel(idx: number, delta: number) {
    const b = getCurrentBlocks()[idx];
    const newLvl = Math.max(1, Math.min(9, b.level + delta));
    if (newLvl !== b.level) updateBlock(b.id, { level: newLvl });
  }

  function addBlockAfter(idx: number, forceVariant?: boolean) {
    const currentBlocks = getCurrentBlocks();
    const prev = currentBlocks[idx];
    const newBlock: any = {
      id: 'blk-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
      level: prev ? prev.level : 1,
      text: '',
    };
    // Auto-assign to active variant if we're in a variant view or forced
    const assignVariant = forceVariant || (activeVariantId && activeVariantId !== 'core');
    if (assignVariant && activeVariantId && activeVariantId !== 'core') {
      newBlock.isVariant = true;
      newBlock.variantId = activeVariantId;
    }
    const newBlocks = [...currentBlocks];
    newBlocks.splice(idx + 1, 0, newBlock);
    setCurrentBlocks(newBlocks);
    setSelectedId(newBlock.id);
    setEditingBlockId(newBlock.id);
  }

  function splitBlockAtCursor(idx: number, cursorPos: number) {
    const currentBlocks = getCurrentBlocks();
    const block = currentBlocks[idx];
    if (!block) return;
    const before = block.text.slice(0, cursorPos);
    const after = block.text.slice(cursorPos);
    const newBlock = {
      id: 'blk-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
      level: block.level,
      text: after,
    };
    const newBlocks = [...currentBlocks];
    newBlocks[idx] = { ...block, text: before };
    newBlocks.splice(idx + 1, 0, newBlock);
    setCurrentBlocks(newBlocks);
    setSelectedId(newBlock.id);
    setEditingBlockId(newBlock.id);
  }

  // Backspace at the very start of a block merges it into the previous text
  // block (Word behavior). Caret lands at the junction point.
  function mergeBlockIntoPrevious(idx: number) {
    if (!canDelete) return;
    const currentBlocks = getCurrentBlocks();
    if (idx <= 0) return;
    const prev = currentBlocks[idx - 1];
    const cur = currentBlocks[idx];
    if (!prev || !cur) return;
    // Only merge plain text into plain text — tables/images/smart tokens keep
    // their block identity.
    if (prev.blockType || cur.blockType) return;
    const junction = (prev.text || '').length;
    const merged = { ...prev, text: (prev.text || '') + (cur.text || '') };
    const newBlocks = [...currentBlocks];
    newBlocks[idx - 1] = merged;
    newBlocks.splice(idx, 1);
    setCurrentBlocks(newBlocks);
    setSelectedId(merged.id);
    setSelectedIds(new Set([merged.id]));
    pendingCaretRef.current = junction;
    setEditingBlockId(merged.id);
  }

  function insertTable(idx: number, rows: number, cols: number) {
    const currentBlocks = getCurrentBlocks();
    const prev = currentBlocks[idx];
    const cells: any[][] = [];
    for (let r = 0; r < rows; r++) {
      const row: any[] = [];
      for (let c = 0; c < cols; c++) row.push({ text: '' });
      cells.push(row);
    }
    const colWidth = Math.floor(100 / cols);
    const colWidths = Array(cols).fill(colWidth);
    colWidths[cols - 1] = 100 - colWidth * (cols - 1);
    const newBlock = {
      id: 'blk-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
      level: prev ? prev.level : 1,
      text: '',
      blockType: 'table' as const,
      tableData: {
        rows,
        cols,
        cells,
        colWidths,
        borderWidth: 1,
        borderColor: '000000',
        borderStyle: 'solid' as const,
        innerBorderWidth: 0.5,
        innerBorderColor: '999999',
        innerBorderStyle: 'solid' as const,
        headerRows: 1,
        headerBgColor: 'E8E8E8',
        headerBgOpacity: 100,
        tableJustify: 'left' as const,
      },
    };
    const newBlocks = [...currentBlocks];
    newBlocks.splice(idx + 1, 0, newBlock);
    setCurrentBlocks(newBlocks);
    setSelectedId(newBlock.id);
  }

  function deleteBlock(idx: number) {
    if (!canDelete) return;
    const currentBlocks = getCurrentBlocks();
    if (currentBlocks.length <= 1) return;
    const newBlocks = currentBlocks.filter((_: any, i: number) => i !== idx);
    setCurrentBlocks(newBlocks);
    setSelectedId(newBlocks[Math.min(idx, newBlocks.length - 1)]?.id || null);
    setEditingBlockId(null);
  }

  // Insert a smart-token block at the given index (after) with the kind +
  // params picked in the SmartTokenPicker. The chip in the editor canvas
  // shows just the declaration; resolution to actual content happens at
  // project-spec compile time via the resolver registry.
  function insertSmartToken(
    idx: number,
    selection: { smartBlockId: string; smartBlockKind: string; smartParams: Record<string, any> },
    forceVariant?: boolean
  ) {
    const currentBlocks = getCurrentBlocks();
    const prev = currentBlocks[idx];
    const newBlock: any = {
      id: 'blk-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
      level: prev ? prev.level : 1,
      text: '',
      blockType: 'smart-token' as const,
      smartBlockId: selection.smartBlockId,
      smartBlockKind: selection.smartBlockKind,
      smartParams: selection.smartParams,
    };
    const assignVariant = forceVariant || (activeVariantId && activeVariantId !== 'core');
    if (assignVariant && activeVariantId && activeVariantId !== 'core') {
      newBlock.isVariant = true;
      newBlock.variantId = activeVariantId;
    }
    const newBlocks = [...currentBlocks];
    newBlocks.splice(idx + 1, 0, newBlock);
    setCurrentBlocks(newBlocks);
    setSelectedId(newBlock.id);
    setDirty(true);
  }

  /** Open the picker; remember which block index to insert after. */
  function openSmartTokenPicker(anchorBlockId: string | -1) {
    smartTokenInsertAnchorRef.current = anchorBlockId;
    setShowSmartTokenPicker(true);
  }

  function handleSmartTokenPickerInsert(selection: {
    smartBlockId: string;
    smartBlockKind: string;
    smartParams: Record<string, any>;
  }) {
    const anchor = smartTokenInsertAnchorRef.current;
    const currentBlocks = getCurrentBlocks();
    let idx: number;
    if (anchor === -1 || anchor == null) {
      idx = currentBlocks.length - 1;
    } else {
      idx = currentBlocks.findIndex((b: any) => b.id === anchor);
      if (idx < 0) idx = currentBlocks.length - 1;
    }
    insertSmartToken(idx, selection);
    setShowSmartTokenPicker(false);
    smartTokenInsertAnchorRef.current = null;
  }

  function duplicateBlock(idx: number) {
    const currentBlocks = getCurrentBlocks();
    const dup = {
      ...JSON.parse(JSON.stringify(currentBlocks[idx])),
      id: 'blk-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
    };
    const newBlocks = [...currentBlocks];
    newBlocks.splice(idx + 1, 0, dup);
    setCurrentBlocks(newBlocks);
  }

  // ─── Multi-selection operations (one-mode editing, 2026-07) ───
  // Every structural verb works on the whole selection, in document order.
  function newBlockId() {
    return (
      'blk-' +
      Date.now().toString(36) +
      Math.random().toString(36).substr(2, 4) +
      Math.random().toString(36).substr(2, 2)
    );
  }

  function duplicateBlocks(ids: string[]) {
    const currentBlocks = getCurrentBlocks();
    const set = new Set(ids);
    const moving = currentBlocks.filter((b: any) => set.has(b.id));
    if (!moving.length) return;
    const lastIdx = currentBlocks.findIndex((b: any) => b.id === moving[moving.length - 1].id);
    const clones = moving.map((b: any) => ({
      ...JSON.parse(JSON.stringify(b)),
      id: newBlockId(),
    }));
    const newBlocks = [...currentBlocks];
    newBlocks.splice(lastIdx + 1, 0, ...clones);
    setCurrentBlocks(newBlocks);
    setSelectedIds(new Set(clones.map((c: any) => c.id)));
    setSelectedId(clones[0].id);
  }

  function changeLevelForIds(ids: string[], delta: number) {
    const set = new Set(ids);
    setCurrentBlocks(
      getCurrentBlocks().map((b: any) =>
        set.has(b.id) ? { ...b, level: Math.max(1, Math.min(9, (b.level || 1) + delta)) } : b
      )
    );
  }

  // Move the whole selection to a drop index (batch splice, order preserved).
  function moveBlocksTo(ids: string[], dropAt: number) {
    const currentBlocks = getCurrentBlocks();
    const set = new Set(ids);
    const moving = currentBlocks.filter((b: any) => set.has(b.id));
    if (!moving.length) return;
    const removedBeforeDrop = currentBlocks
      .slice(0, dropAt)
      .filter((b: any) => set.has(b.id)).length;
    const remaining = currentBlocks.filter((b: any) => !set.has(b.id));
    const insertAt = Math.max(0, Math.min(remaining.length, dropAt - removedBeforeDrop));
    remaining.splice(insertAt, 0, ...moving);
    setCurrentBlocks(remaining);
    setSelectedId(moving[0].id);
  }

  function assignBlocksToVariant(ids: string[], variantId: string) {
    const set = new Set(ids);
    setBlocks((prev) => prev.map((b) => (set.has(b.id) ? { ...b, variantId } : b)));
    setDirty(true);
  }

  // Toggle direction comes from the anchor block so a mixed selection lands in
  // one predictable state instead of flipping each block independently.
  function toggleBlocksCoreVariant(ids: string[], becomingVariant: boolean) {
    const set = new Set(ids);
    const vId = activeVariantId && activeVariantId !== 'core' ? activeVariantId : undefined;
    setBlocks((prev) =>
      prev.map((b) => {
        if (!set.has(b.id)) return b;
        return becomingVariant
          ? { ...b, isVariant: true, variantId: vId }
          : { ...b, isVariant: false, variantId: undefined };
      })
    );
    setDirty(true);
  }

  function moveBlock(fromIdx: number, toIdx: number) {
    if (fromIdx === toIdx) return;
    const currentBlocks = [...getCurrentBlocks()];
    const [moved] = currentBlocks.splice(fromIdx, 1);
    currentBlocks.splice(toIdx > fromIdx ? toIdx - 1 : toIdx, 0, moved);
    setCurrentBlocks(currentBlocks);
    setDragOverIdx(null);
    setDragFromIdx(null);
  }

  function moveBlockToLevel(fromIdx: number, toIdx: number, newLevel: number) {
    const currentBlocks = [...getCurrentBlocks()];
    const [moved] = currentBlocks.splice(fromIdx, 1);
    moved.level = newLevel;
    const insertAt = toIdx > fromIdx ? toIdx - 1 : toIdx;
    currentBlocks.splice(insertAt, 0, moved);
    setCurrentBlocks(currentBlocks);
    setSelectedId(moved.id);
    setShowLevelPicker(null);
    setDragOverIdx(null);
    setDragFromIdx(null);
  }

  /** Toggle whether a core block is hidden from a specific variant's view.
   *  Used by the right-click menu while the editor is inside a variant — lets
   *  the user say "this core block doesn't apply to Variant X" without
   *  deleting it from core. Compile-time filter in CompileSpecs strips these
   *  when the target variant matches. */
  function toggleCoreExcludedFromVariant(blockId: string, variantId: string) {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        const current: string[] = b.excludedFromVariants || [];
        const next = current.includes(variantId)
          ? current.filter((v) => v !== variantId)
          : [...current, variantId];
        return { ...b, excludedFromVariants: next.length ? next : undefined };
      })
    );
    setDirty(true);
  }

  /** Assign a standalone variant block to a specific variant */
  function assignBlockToVariant(blockId: string, variantId: string) {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        // A variant block lives in exactly one variant. Re-assigning just
        // replaces the variantId outright (no multi-variant membership).
        return { ...b, variantId };
      })
    );
    setDirty(true);
  }

  // ─── Core/Variant toggle for individual blocks ───
  function toggleBlockCoreVariant(blockId: string) {
    const block = blocks.find((b: any) => b.id === blockId);
    if (!block) return;
    const becomingVariant = !block.isVariant;
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        // Variant blocks live in exactly one variant. When toggling ON we pick
        // the active variant if the user is inside one; otherwise the block is
        // flagged variant without a target (user must pick via the submenu).
        if (becomingVariant) {
          const vId = activeVariantId && activeVariantId !== 'core' ? activeVariantId : undefined;
          return { ...b, isVariant: true, variantId: vId };
        }
        return { ...b, isVariant: false, variantId: undefined };
      })
    );
    setDirty(true);
  }

  function isBlockVariant(blockId: string): boolean {
    const block = blocks.find((b: any) => b.id === blockId);
    return !!block?.isVariant;
  }

  /** Variant label for a block. Variant blocks live in exactly one variant. */
  function getVariantLabel(block: any): string {
    if (!block.isVariant) return '';
    if (!block.variantId) return 'V?';
    const idx = variants.findIndex((v) => v.id === block.variantId);
    return idx >= 0 ? `V${idx + 1}` : 'V?';
  }

  // ─── Variant helpers ───

  const [showVariantModal, setShowVariantModal] = React.useState(false);
  const [newVariantName, setNewVariantName] = React.useState('');

  function addVariant() {
    if (!newVariantName.trim()) return;
    const id = 'var-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4);
    const v: Variant = {
      id,
      name: newVariantName.trim(),
      version: 1,
      status: 'draft',
      lastModified: new Date().toISOString(),
      modifiedBy: userName,
    };
    setVariants((prev) => [...prev, v]);
    setNewVariantName('');
    setShowVariantModal(false);
    setDirty(true);
  }

  function deleteVariant(id: string) {
    setVariants((prev) => prev.filter((v) => v.id !== id));
    if (activeVariantId === id) setActiveVariantId(null);
    setDirty(true);
  }

  function renameVariant(id: string, name: string) {
    setVariants((prev) => prev.map((v) => (v.id === id ? { ...v, name } : v)));
    setDirty(true);
  }

  /** Which blocks are visible for the active variant context.
   *  - "All" view (activeVariantId null): everything — every core block,
   *    every variant block (regardless of target), every module.
   *  - "Core" view: only non-variant blocks, filtered to those NOT present in
   *    excludedFromVariants for any variant (core items are always visible in
   *    core; per-variant exclusions only hide them inside a variant view).
   *  - Variant view: core blocks that the active variant hasn't excluded,
   *    plus variant blocks where variantId === activeVariantId, plus module
   *    blocks whose module is assigned to the active variant. */
  function getVisibleBlockIds(): Set<string> | null {
    if (!activeVariantId) return null; // "All" view — show everything
    if (activeVariantId === 'core') {
      return new Set(blocks.filter((b: any) => !b.isVariant).map((b: any) => b.id));
    }
    return new Set(
      blocks
        .filter((b: any) => {
          if (!b.isVariant) {
            // v2.12.1 — admit excluded core blocks into the visible set so
            // the downstream render filter (which gates on
            // viewOpts.showExcludedFromVariant) can render them with
            // strikethrough + the EXCLUDED chip when the user opts in.
            return true;
          }
          return b.variantId === activeVariantId;
        })
        .map((b: any) => b.id)
    );
  }

  const visibleBlockIds = getVisibleBlockIds();

  // Caret hand-off: remember where in the text the user clicked (computed from
  // the display span at mousedown, before it swaps for the textarea) so the
  // editor opens with the caret at the click point — Word behavior, not
  // caret-at-end. Chromium: caretRangeFromPoint; Firefox: caretPositionFromPoint.
  const pendingCaretRef = React.useRef<number | null>(null);
  // Same idea for tables: the clicked cell gets the focus when edit mode mounts.
  const pendingCellRef = React.useRef<{ r: number; c: number } | null>(null);
  function captureCaretFromPoint(rowEl: HTMLElement, x: number, y: number) {
    try {
      const doc = document as any;
      let node: Node | null = null;
      let offset = 0;
      if (doc.caretRangeFromPoint) {
        const r = doc.caretRangeFromPoint(x, y);
        if (r) {
          node = r.startContainer;
          offset = r.startOffset;
        }
      } else if (doc.caretPositionFromPoint) {
        const p = doc.caretPositionFromPoint(x, y);
        if (p) {
          node = p.offsetNode;
          offset = p.offset;
        }
      }
      if (!node || !rowEl.contains(node)) {
        pendingCaretRef.current = null;
        return;
      }
      // Absolute offset = lengths of all text nodes before the hit node (the
      // find-highlight <mark>s split the text into several nodes).
      const walker = document.createTreeWalker(rowEl, NodeFilter.SHOW_TEXT);
      let abs = 0;
      let n: Node | null = walker.nextNode();
      while (n) {
        if (n === node) {
          abs += offset;
          pendingCaretRef.current = abs;
          return;
        }
        abs += (n.textContent || '').length;
        n = walker.nextNode();
      }
      pendingCaretRef.current = null;
    } catch {
      pendingCaretRef.current = null;
    }
  }

  // The outline IS the group: a block's subtree = every following block with a
  // deeper level, until the next sibling/uncle. Selecting a heading's handle
  // grabs the whole branch so sections move/copy/variant as one unit.
  function subtreeIdsOf(blockId: string): string[] {
    const currentBlocks = getCurrentBlocks();
    const idx = currentBlocks.findIndex((b: any) => b.id === blockId);
    if (idx < 0) return [blockId];
    const lvl = currentBlocks[idx].level || 1;
    const ids = [blockId];
    for (let i = idx + 1; i < currentBlocks.length; i++) {
      if ((currentBlocks[i].level || 1) <= lvl) break;
      ids.push(currentBlocks[i].id);
    }
    return ids;
  }

  // Gutter-handle click: select WITHOUT entering the text (structure work).
  // Plain click = block + its subtree; Alt+click = just this block.
  function handleHandleClick(blockId: string, e: React.MouseEvent) {
    e.stopPropagation();
    setContextMenu(null);
    setEditingBlockId(null);
    setShowLevelPicker(null);
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      handleBlockClick(blockId, e); // modifier paths already do pure selection
      return;
    }
    const ids = e.altKey ? [blockId] : subtreeIdsOf(blockId);
    setSelectedId(blockId);
    setSelectedIds(new Set(ids));
    setLastClickedId(blockId);
  }

  function handleBlockClick(blockId: string, e: React.MouseEvent) {
    // One-mode: a plain click on the text enters editing right there; modifier
    // clicks (Shift/Ctrl) build the multi-selection without entering the text.
    if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
      setSelectedId(blockId);
      setSelectedIds(new Set([blockId]));
      setLastClickedId(blockId);
      setEditingBlockId(blockId);
      return;
    }

    if (e.shiftKey && lastClickedId) {
      const ids = blocks.map((b: any) => b.id);
      const lastIdx = ids.indexOf(lastClickedId);
      const curIdx = ids.indexOf(blockId);
      if (lastIdx >= 0 && curIdx >= 0) {
        const start = Math.min(lastIdx, curIdx);
        const end = Math.max(lastIdx, curIdx);
        const rangeIds = ids.slice(start, end + 1);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          rangeIds.forEach((id) => next.add(id));
          return next;
        });
      }
    } else if (e.ctrlKey || e.metaKey) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(blockId)) next.delete(blockId);
        else next.add(blockId);
        return next;
      });
    } else {
      setSelectedIds(new Set([blockId]));
    }
    setSelectedId(blockId);
    setLastClickedId(blockId);
    if (editingBlockId !== blockId) setEditingBlockId(null);
    setShowLevelPicker(null);
    // Modifier-click selects BLOCKS, not text — clear the native browser
    // text selection a Shift/Ctrl click leaves smeared across the rows
    // (the selection bars are the only selection signal).
    window.getSelection()?.removeAllRanges();
  }

  function buildNumberedBlocks(blockList: any[]) {
    // Library module editor shows placeholder level tags instead of real
    const counters = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    let currentPart = 0;
    let lastPrefix = '';
    return blockList.map((b) => {
      // Continuation blocks share the previous block's prefix — don't increment
      if (b.continuation) {
        return { ...b, prefix: '' };
      }
      for (let i = b.level; i < 9; i++) counters[i] = 0;
      counters[b.level - 1]++;
      if (b.level === 1) currentPart = counters[0];
      let prefix = '';
      const n = counters[b.level - 1];
      switch (b.level) {
        case 1:
          prefix = 'PART ' + n + ' -';
          break;
        case 2:
          prefix = currentPart + '.' + String(n).padStart(2, '0');
          break;
        case 3:
          prefix = String.fromCharCode(64 + Math.min(n, 26)) + '.';
          break;
        case 4:
          prefix = n + '.';
          break;
        case 5:
          prefix = String.fromCharCode(96 + Math.min(n, 26)) + '.';
          break;
        case 6:
          prefix = n + ')';
          break;
        case 7:
          prefix = String.fromCharCode(96 + Math.min(n, 26)) + ')';
          break;
        case 8:
          prefix = '(' + n + ')';
          break;
        case 9:
          prefix = '(' + String.fromCharCode(96 + Math.min(n, 26)) + ')';
          break;
      }
      lastPrefix = prefix;
      return { ...b, prefix };
    });
  }

  function scrollToBlock(blockId: string) {
    const el = document.getElementById('block-' + blockId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // ─── Find & Replace helpers ───
  const findMatches = React.useMemo(() => {
    if (!findText || !showFind) return [];
    const matches: { blockId: string; blockIdx: number; startIdx: number }[] = [];
    const search = findMatchCase ? findText : findText.toLowerCase();
    const cb = getCurrentBlocks();
    const visible = visibleBlockIds;
    cb.forEach((b: any, i: number) => {
      if (visible && !visible.has(b.id)) return;
      // Search text blocks
      if (b.blockType !== 'table' && b.blockType !== 'image') {
        const text = findMatchCase ? b.text || '' : (b.text || '').toLowerCase();
        let pos = 0;
        while ((pos = text.indexOf(search, pos)) !== -1) {
          matches.push({ blockId: b.id, blockIdx: i, startIdx: pos });
          pos += search.length;
        }
      }
      // Search table cells
      if (b.blockType === 'table' && b.tableData?.cells) {
        b.tableData.cells.forEach((row: any[]) =>
          row.forEach((cell: any) => {
            const text = findMatchCase ? cell.text || '' : (cell.text || '').toLowerCase();
            let pos = 0;
            while ((pos = text.indexOf(search, pos)) !== -1) {
              matches.push({ blockId: b.id, blockIdx: i, startIdx: pos });
              pos += search.length;
            }
          })
        );
      }
    });
    return matches;
  }, [findText, findMatchCase, showFind, blocks, activeVariantId]);

  // Clamp match index
  React.useEffect(() => {
    if (findMatches.length === 0) {
      setFindMatchIdx(0);
      return;
    }
    if (findMatchIdx >= findMatches.length) setFindMatchIdx(0);
  }, [findMatches.length]);

  // Navigate to current match
  React.useEffect(() => {
    if (findMatches.length > 0 && findMatchIdx < findMatches.length) {
      const m = findMatches[findMatchIdx];
      setSelectedId(m.blockId);
      setSelectedIds(new Set([m.blockId]));
      scrollToBlock(m.blockId);
    }
  }, [findMatchIdx, findMatches]);

  const findMatchBlockIds = React.useMemo(
    () => new Set(findMatches.map((m) => m.blockId)),
    [findMatches]
  );
  const findCurrentBlockId =
    findMatches.length > 0 && findMatchIdx < findMatches.length
      ? findMatches[findMatchIdx].blockId
      : null;

  function findNext() {
    if (findMatches.length === 0) return;
    setFindMatchIdx((i) => (i + 1) % findMatches.length);
  }
  function findPrev() {
    if (findMatches.length === 0) return;
    setFindMatchIdx((i) => (i - 1 + findMatches.length) % findMatches.length);
  }
  function replaceOne() {
    if (findMatches.length === 0 || findMatchIdx >= findMatches.length) return;
    const m = findMatches[findMatchIdx];
    const b = getCurrentBlocks().find((bl: any) => bl.id === m.blockId);
    if (!b) return;
    if (b.blockType === 'table') {
      // Replace first match in table cells
      const newCells = b.tableData.cells.map((row: any[]) =>
        row.map((cell: any) => {
          const text = cell.text || '';
          const search = findMatchCase ? findText : findText.toLowerCase();
          const idx = (findMatchCase ? text : text.toLowerCase()).indexOf(search);
          if (idx === -1) return cell;
          return {
            ...cell,
            text: text.slice(0, idx) + replaceText + text.slice(idx + findText.length),
          };
        })
      );
      updateBlock(m.blockId, { tableData: { ...b.tableData, cells: newCells } });
    } else {
      const text = b.text || '';
      const newText =
        text.slice(0, m.startIdx) + replaceText + text.slice(m.startIdx + findText.length);
      updateBlock(m.blockId, { text: newText });
    }
    setDirty(true);
  }
  function replaceAll() {
    if (findMatches.length === 0) return;
    const bs = getCurrentBlocks();
    const search = findMatchCase ? findText : findText.toLowerCase();
    const updated = bs.map((b: any) => {
      if (b.blockType === 'table' && b.tableData?.cells) {
        const newCells = b.tableData.cells.map((row: any[]) =>
          row.map((cell: any) => {
            const text = cell.text || '';
            const re = new RegExp(
              findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
              findMatchCase ? 'g' : 'gi'
            );
            return { ...cell, text: text.replace(re, replaceText) };
          })
        );
        return { ...b, tableData: { ...b.tableData, cells: newCells } };
      }
      if (b.text) {
        const re = new RegExp(
          findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
          findMatchCase ? 'g' : 'gi'
        );
        return { ...b, text: b.text.replace(re, replaceText) };
      }
      return b;
    });
    setCurrentBlocks(updated);
    setDirty(true);
    toast(`Replaced ${findMatches.length} matches`);
  }

  const allBlocks = getCurrentBlocks();
  const currentBlocks = visibleBlockIds
    ? allBlocks.filter((b: any) => visibleBlockIds.has(b.id))
    : allBlocks;
  const numberedBlocks = buildNumberedBlocks(currentBlocks);
  const selected = allBlocks.find((b: any) => b.id === selectedId);
  const canUndo = historyIdxRef.current > 0;
  const canRedo = historyIdxRef.current < historyRef.current.length - 1;

  if (loading) {
    // Skeleton mirrors the editor chrome (header bar + paper column) so the
    // layout doesn't jump when content lands. Pulse keyframe lives in base.css.
    const shimmer = (w: number | string, h: number, delay = 0): React.CSSProperties => ({
      width: w,
      height: h,
      borderRadius: 'var(--radius-sm)',
      background: 'var(--sa)',
      animation: 'pulse 1.4s ease-in-out infinite',
      animationDelay: `${delay}ms`,
      flexShrink: 0,
    });
    return (
      <div
        aria-busy="true"
        aria-label="Loading spec master"
        style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
      >
        {/* Header bar skeleton */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-4)',
            borderBottom: '1px solid var(--bd)',
            background: 'var(--sf)',
          }}
        >
          <div style={shimmer(64, 28)} />
          <div style={shimmer(90, 16, 80)} />
          <div style={shimmer(200, 16, 120)} />
          <div style={{ flex: 1 }} />
          <div style={shimmer(72, 28, 160)} />
          <div style={shimmer(56, 28, 200)} />
          <div style={shimmer(88, 28, 240)} />
        </div>
        {/* Paper column skeleton */}
        <div
          style={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            padding: 'var(--space-5)',
            background: 'var(--bg)',
          }}
        >
          <div
            style={{
              width: 680,
              maxWidth: '100%',
              background: 'var(--sf)',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-sm)',
              padding: 'var(--space-6) var(--space-7)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
            }}
          >
            <div style={shimmer('45%', 18)} />
            <div style={shimmer('70%', 12, 80)} />
            <div style={shimmer('90%', 12, 160)} />
            <div style={shimmer('85%', 12, 240)} />
            <div style={shimmer('60%', 12, 320)} />
            <div style={{ height: 'var(--space-3)' }} />
            <div style={shimmer('40%', 14, 400)} />
            <div style={shimmer('88%', 12, 480)} />
            <div style={shimmer('92%', 12, 560)} />
            <div style={shimmer('75%', 12, 640)} />
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    const isLockError = error.includes('locked by');
    const canOverride = canOverrideDraft;
    return (
      <div
        role="alert"
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--space-6)',
        }}
      >
        <div style={{ maxWidth: 440, textAlign: 'center' }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--tx)',
              marginBottom: 'var(--space-2)',
            }}
          >
            This spec master couldn’t load
          </div>
          <div style={{ fontSize: 13, color: 'var(--dg)', marginBottom: 'var(--space-2)' }}>
            {error}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--tm)',
              lineHeight: 1.5,
              marginBottom: 'var(--space-4)',
            }}
          >
            {isLockError
              ? 'Another editor holds the draft lock. Ask them to finish, or override the draft if you have permission.'
              : 'Go back to the library and open it again — recent changes sync automatically.'}
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'center' }}>
            <Button variant="outline" onClick={onBack}>
              Back to Library
            </Button>
            {isLockError && canOverride && (
              <Button
                variant="outline"
                style={{ color: 'var(--dg)', borderColor: 'var(--dg)' }}
                onClick={async () => {
                  if (
                    !confirm(
                      "This will discard the other user's draft and unlock the spec. Are you sure?"
                    )
                  )
                    return;
                  try {
                    await SpecService.forceOverrideDraft(masterId);
                    toast('Draft overridden — spec unlocked');
                    setError(null);
                    setLoading(true);
                    await initEditor();
                  } catch (err: any) {
                    toast('Override failed: ' + (err.message || err), true);
                  }
                }}
              >
                Override Draft
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
  // Render guard: either master or draft means we have content.
  // v2.11.11 added the `master` branch so spec masters render their published
  // baseline read-only-ish until the first edit triggers the lazy checkout
  // that creates `draft`.
  if (!draft && !master)
    return (
      <div style={{ padding: 'var(--space-7)', textAlign: 'center', color: 'var(--tm)' }}>
        No draft loaded
      </div>
    );

  // v2.11.11+: when no draft exists yet (lazy mode), fall through to the
  // published master so title + CSI render before first edit.
  const specTitle = draft?.title || master?.title || 'Untitled';
  const specCsi = draft?.csiNumber || master?.csiNumber || '';

  const levelStyle = (block: any): React.CSSProperties => {
    const base: React.CSSProperties = { lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" };
    switch (block.level) {
      case 1:
        return {
          ...base,
          fontSize: 13,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '.02em',
          marginTop: 16,
        };
      case 2:
        return { ...base, fontSize: 13, fontWeight: 600, textTransform: 'uppercase' };
      case 3:
        return { ...base, fontSize: 13, fontWeight: 500 };
      default:
        return { ...base, fontSize: 13, fontWeight: 400 };
    }
  };

  // Publish-request status (from the master draft).
  const prStatus = draft?.publishRequest?.status;
  const isDragging = blockDragIdx !== null;
  const pageW = 680;
  const pagePadH = 60;

  // ─── Document Map data ───
  const docMapBlocks = buildNumberedBlocks(currentBlocks);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 16px',
          borderBottom: '1px solid var(--bd)',
          background: 'var(--sf)',
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="ghost"
          size="sm"
          className="gap-1 px-2 text-sm font-normal text-muted"
          onClick={() => {
            // Masters: no edits → leave silently. The exit guard exists to
            // protect unsaved work; firing it on every back-out was the
            // v2.5+ pain point.
            if (!dirty) {
              onBack();
              return;
            }
            setShowExitGuard(true);
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          {/* CSI number + em-dash separator. */}
          {editingCsi ? (
            <input
              autoFocus
              value={specCsi}
              onChange={(e) => {
                setDraft((p) => (p ? { ...p, csiNumber: e.target.value } : p));
                setDirty(true);
              }}
              onBlur={() => setEditingCsi(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setEditingCsi(false);
                e.stopPropagation();
              }}
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--ac)',
                background: 'var(--ib)',
                border: '1px solid var(--ac)',
                borderRadius: 'var(--radius-sm)',
                padding: '2px 8px',
                width: 100,
              }}
            />
          ) : (
            <span
              onClick={() => setEditingCsi(true)}
              title="Click to edit CSI number"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--ac)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {specCsi}
            </span>
          )}
          <span style={{ color: 'var(--tm)', fontSize: 14, flexShrink: 0 }}>&mdash;</span>
          {editingTitle ? (
            <input
              autoFocus
              value={specTitle}
              onChange={(e) => {
                setDraft((p) => (p ? { ...p, title: e.target.value } : p));
                setDirty(true);
              }}
              onBlur={() => setEditingTitle(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setEditingTitle(false);
                e.stopPropagation();
              }}
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: 'var(--tx)',
                background: 'var(--ib)',
                border: '1px solid var(--ac)',
                borderRadius: 'var(--radius-sm)',
                padding: '2px 8px',
                flex: 1,
                minWidth: 200,
              }}
            />
          ) : (
            <span
              onClick={() => setEditingTitle(true)}
              title="Click to edit title"
              style={{
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {specTitle}
            </span>
          )}
          <span
            style={{
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(var(--acr),0.12)',
              color: 'var(--ac)',
              fontWeight: 600,
              letterSpacing: '.03em',
              flexShrink: 0,
            }}
          >
            DRAFT
          </span>
          <span
            style={{
              fontSize: 10,
              padding: '2px 8px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--sa)',
              color: 'var(--tm)',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            v{draft?.baseVersion ?? 0}
          </span>
          {prStatus === 'pending' && (
            <span
              style={{
                fontSize: 11,
                padding: '2px 8px',
                borderRadius: 'var(--radius-full)',
                fontWeight: 600,
                letterSpacing: '.03em',
                background: 'color-mix(in srgb, var(--color-warning) 15%, transparent)',
                color: 'color-mix(in srgb, var(--color-warning) 60%, var(--color-text))',
                flexShrink: 0,
              }}
            >
              PR Pending
            </span>
          )}
          {prStatus === 'rejected' && (
            <span
              style={{
                fontSize: 11,
                padding: '2px 8px',
                borderRadius: 'var(--radius-full)',
                fontWeight: 600,
                letterSpacing: '.03em',
                background: 'color-mix(in srgb, var(--color-danger) 12%, transparent)',
                color: 'var(--dg)',
                flexShrink: 0,
              }}
            >
              PR Rejected
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 3 }}>
          <Button
            variant="ghost"
            size="sm"
            className="w-8 px-0 text-muted"
            onClick={undo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            aria-label="Undo (Ctrl+Z)"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-8 px-0 text-muted"
            onClick={redo}
            disabled={!canRedo}
            title="Redo (Ctrl+Y)"
            aria-label="Redo (Ctrl+Y)"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" />
            </svg>
          </Button>
          <div style={{ width: 1, height: 20, background: 'var(--bd)', margin: '0 2px' }} />
          {/* Select/Write mode toggle removed (2026-07): one-mode editing —
              click text to type, use the gutter handle to select/drag. */}
          {/* View menu — persistent visibility toggles (see ViewOpts). Placed
              next to Diff/Versions so the user can reach all review/display
              controls in one cluster. Wraps the old Diff button as one item
              inside the menu ("Track changes"). */}
          <div ref={viewMenuRef} style={{ position: 'relative' }}>
            <Button
              variant="ghost"
              size="sm"
              className={
                showViewMenu || diffMode
                  ? 'bg-primary-soft px-2 text-sm font-semibold text-primary hover:bg-primary-soft'
                  : 'px-2 text-sm font-normal text-muted'
              }
              onClick={() => setShowViewMenu((v) => !v)}
              title="Display options"
            >
              View
            </Button>
            {showViewMenu && (
              <ViewMenu
                opts={viewOpts}
                diffMode={diffMode}
                onToggle={(key) => setViewOpts((prev) => ({ ...prev, [key]: !prev[key] }))}
                onToggleTrackChanges={() => {
                  if (diffMode) setDiffMode(false);
                  else handleShowDiff();
                }}
                onClose={() => setShowViewMenu(false)}
              />
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 px-2 text-sm font-normal text-muted"
            onClick={() => openSmartTokenPicker(selectedId || -1)}
            title="Insert a Smart Block at the selected position"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ flexShrink: 0 }}
            >
              <path d="M12 3l1.9 5.8L20 11l-5.8 1.9L12 19l-1.9-6.1L4 11l6.1-2.2z" />
            </svg>
            Smart Block
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="px-2 text-sm font-normal text-muted"
            onClick={() => setShowVersionHistory(true)}
            title="Version history"
          >
            Versions
          </Button>
          <HistoryButton entries={master?.editHistory || []} />
          <Button
            variant="ghost"
            size="sm"
            className="px-2 text-sm font-normal text-muted"
            onClick={() => {
              setSaveAsTitle((draft!.title || '') + ' (Copy)');
              setShowSaveAsModal(true);
            }}
          >
            Save As
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="px-2 text-sm font-normal text-danger hover:text-danger"
            onClick={handleDiscard}
            title="Discard draft"
          >
            Discard
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={
              dirty
                ? 'border-primary px-2 text-sm font-semibold text-primary'
                : 'px-2 text-sm font-semibold text-muted'
            }
            onClick={handleSave}
          >
            Save {dirty && '*'}
          </Button>
          <Button
            size="sm"
            className="px-3 text-sm font-semibold"
            onClick={handlePublish}
            disabled={prStatus === 'pending'}
          >
            {canDirectPublish
              ? 'Publish'
              : prStatus === 'pending'
                ? 'PR Pending...'
                : 'Submit for Review'}
          </Button>
        </div>
      </div>

      {/* Presence banner — who else is editing this spec */}
      {presence.length > 0 && (
        <div
          style={{
            padding: 'var(--space-1) 20px',
            background: 'var(--color-primary-soft)',
            borderBottom: '1px solid color-mix(in srgb, var(--color-primary) 25%, transparent)',
            fontSize: 11,
            color: 'var(--color-primary-text)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            flexShrink: 0,
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <span>
            {presence.map((p) => p.userName).join(', ')} {presence.length === 1 ? 'is' : 'are'} also
            editing
            {presence.some((p) => p.variantId) && (
              <>
                {' '}
                —{' '}
                {presence
                  .map((p) => {
                    if (!p.variantId || p.variantId === 'core') return `${p.userName} on Core`;
                    const vi = variants.findIndex((v) => v.id === p.variantId);
                    return vi >= 0 ? `${p.userName} on V${vi + 1}` : p.userName;
                  })
                  .join(', ')}
              </>
            )}
          </span>
        </div>
      )}

      {/* Rejected PR notice (draft mode only) */}
      {prStatus === 'rejected' && draft?.publishRequest && (
        <div
          style={{
            padding: '6px 20px',
            background: 'color-mix(in srgb, var(--color-danger) 8%, transparent)',
            borderBottom: '1px solid color-mix(in srgb, var(--color-danger) 20%, transparent)',
            fontSize: 11,
            color: 'var(--dg)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <strong>PR rejected</strong>
          {draft.publishRequest.reviewedBy && <span>by {draft.publishRequest.reviewedBy}</span>}
          {draft.publishRequest.reviewNote && (
            <span style={{ color: 'var(--tx)' }}>— "{draft.publishRequest.reviewNote}"</span>
          )}
        </div>
      )}

      {/* Zoom moved to the floating ZoomCluster on the canvas (Formas pattern);
          block/selection counts live in the view-mode bar. */}

      {/* Variant selector bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 12px',
          background: 'var(--sf)',
          borderBottom: '1px solid var(--bd)',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--tm)', marginRight: 2 }}>
          View:
        </span>
        {/* All / Core tabs */}
        {[
          { id: null as string | null, label: 'All' },
          { id: 'core' as string | null, label: 'Core' },
        ].map((opt) => {
          const tabPresence = presence.filter((p) =>
            opt.id === null
              ? true
              : opt.id === 'core'
                ? p.variantId === 'core'
                : p.variantId === opt.id
          );
          return (
            <div key={opt.id || 'all'} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <button
                onClick={() => setActiveVariantId(opt.id)}
                style={{
                  fontSize: 10,
                  padding: '2px 8px',
                  borderRadius: 4,
                  border:
                    activeVariantId === opt.id ? '1px solid var(--ac)' : '1px solid var(--bd)',
                  background: activeVariantId === opt.id ? 'rgba(var(--acr),0.12)' : 'transparent',
                  color: activeVariantId === opt.id ? 'var(--ac)' : 'var(--tm)',
                  cursor: 'pointer',
                  fontWeight: activeVariantId === opt.id ? 600 : 400,
                }}
              >
                {opt.label}
              </button>
              {opt.id !== null &&
                tabPresence.length > 0 &&
                tabPresence.map((p, pi) => (
                  <span
                    key={pi}
                    title={`${p.userName} is editing`}
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: 'var(--color-primary)',
                      color: 'var(--color-text-inverse)',
                      fontSize: 10,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      cursor: 'default',
                      border: '2px solid var(--sf)',
                    }}
                  >
                    {(p.userName || '?').slice(0, 2).toUpperCase()}
                  </span>
                ))}
            </div>
          );
        })}
        {variants.length > 0 && (
          <div style={{ width: 1, height: 14, background: 'var(--bd)', margin: '0 2px' }} />
        )}
        {/* Variant tabs with delete + presence — V1, V2, V3 sequential */}
        {variants.map((v, vi) => {
          const tabPresence = presence.filter((p) => p.variantId === v.id);
          return (
            <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0,
                  borderRadius: 4,
                  border: activeVariantId === v.id ? '1px solid var(--ac)' : '1px solid var(--bd)',
                  background: activeVariantId === v.id ? 'rgba(var(--acr),0.12)' : 'transparent',
                  overflow: 'hidden',
                }}
              >
                <button
                  onClick={() => setActiveVariantId(v.id)}
                  style={{
                    fontSize: 10,
                    padding: '2px 8px',
                    border: 'none',
                    background: 'transparent',
                    color: activeVariantId === v.id ? 'var(--ac)' : 'var(--tm)',
                    cursor: 'pointer',
                    fontWeight: activeVariantId === v.id ? 600 : 400,
                  }}
                >
                  V{vi + 1}: {v.name}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Delete variant "V${vi + 1}: ${v.name}"?`)) deleteVariant(v.id);
                  }}
                  title={`Delete V${vi + 1}: ${v.name}`}
                  style={{
                    fontSize: 11,
                    padding: '2px 4px',
                    border: 'none',
                    borderLeft: '1px solid var(--bd)',
                    background: 'transparent',
                    color: 'var(--tm)',
                    cursor: 'pointer',
                    opacity: 0.4,
                    lineHeight: 1,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.color = 'var(--dg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.4';
                    e.currentTarget.style.color = 'var(--tm)';
                  }}
                >
                  &times;
                </button>
              </div>
              {tabPresence.map((p, pi) => (
                <span
                  key={pi}
                  title={`${p.userName} is editing`}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: 'var(--color-primary)',
                    color: 'var(--color-text-inverse)',
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    cursor: 'default',
                    border: '2px solid var(--sf)',
                  }}
                >
                  {(p.userName || '?').slice(0, 2).toUpperCase()}
                </span>
              ))}
            </div>
          );
        })}
        <button
          onClick={() => setShowVariantModal(true)}
          style={{
            fontSize: 10,
            padding: '2px 6px',
            borderRadius: 4,
            border: '1px dashed var(--bd)',
            background: 'transparent',
            color: 'var(--tm)',
            cursor: 'pointer',
          }}
        >
          +
        </button>
        {activeVariantId &&
          activeVariantId !== 'core' &&
          (() => {
            const vi = variants.findIndex((v) => v.id === activeVariantId);
            return (
              <span style={{ fontSize: 11, color: 'var(--tm)', marginLeft: 'auto' }}>
                Showing core + V{vi + 1}: {variants[vi]?.name} ({currentBlocks.length}/
                {allBlocks.length})
              </span>
            );
          })()}
        {activeVariantId === 'core' && (
          <span style={{ fontSize: 11, color: 'var(--tm)', marginLeft: 'auto' }}>
            Core only ({currentBlocks.length}/{allBlocks.length})
          </span>
        )}
      </div>

      {/* Find & Replace bar */}
      {showFind && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            padding: '6px 12px',
            background: 'var(--sf)',
            borderBottom: '1px solid var(--bd)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--tm)"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={findInputRef}
              autoFocus
              placeholder="Find..."
              value={findText}
              onChange={(e) => {
                setFindText(e.target.value);
                setFindMatchIdx(0);
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  findNext();
                }
                if (e.key === 'Enter' && e.shiftKey) {
                  e.preventDefault();
                  findPrev();
                }
                if (e.key === 'Escape') {
                  setShowFind(false);
                  setShowReplace(false);
                  setFindText('');
                }
              }}
              style={{
                flex: 1,
                padding: '4px 8px',
                fontSize: 13,
                border: '1px solid var(--bd)',
                borderRadius: 4,
                background: 'var(--ib)',
                color: 'var(--tx)',
                outline: 'none',
                minWidth: 0,
              }}
            />
            <span
              style={{
                fontSize: 11,
                color: 'var(--tm)',
                whiteSpace: 'nowrap',
                minWidth: 50,
                textAlign: 'center',
              }}
            >
              {findText
                ? `${findMatches.length > 0 ? findMatchIdx + 1 : 0} / ${findMatches.length}`
                : ''}
            </span>
            <button
              onClick={findPrev}
              disabled={findMatches.length === 0}
              title="Previous (Shift+Enter)"
              style={{
                padding: '3px 6px',
                borderRadius: 3,
                border: '1px solid var(--bd)',
                background: 'var(--sa)',
                color: 'var(--tx)',
                cursor: findMatches.length > 0 ? 'pointer' : 'default',
                opacity: findMatches.length > 0 ? 1 : 0.3,
                fontSize: 13,
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              &#8593;
            </button>
            <button
              onClick={findNext}
              disabled={findMatches.length === 0}
              title="Next (Enter)"
              style={{
                padding: '3px 6px',
                borderRadius: 3,
                border: '1px solid var(--bd)',
                background: 'var(--sa)',
                color: 'var(--tx)',
                cursor: findMatches.length > 0 ? 'pointer' : 'default',
                opacity: findMatches.length > 0 ? 1 : 0.3,
                fontSize: 13,
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              &#8595;
            </button>
            <button
              onClick={() => setFindMatchCase((c) => !c)}
              title="Match case"
              style={{
                padding: '3px 6px',
                borderRadius: 3,
                border: findMatchCase ? '1px solid var(--ac)' : '1px solid var(--bd)',
                background: findMatchCase ? 'rgba(var(--acr),0.12)' : 'var(--sa)',
                color: findMatchCase ? 'var(--ac)' : 'var(--tm)',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              Aa
            </button>
            <button
              onClick={() => {
                if (!showReplace) {
                  setShowReplace(true);
                } else {
                  setShowReplace(false);
                }
              }}
              title="Toggle replace"
              style={{
                padding: '3px 6px',
                borderRadius: 3,
                border: showReplace ? '1px solid var(--ac)' : '1px solid var(--bd)',
                background: showReplace ? 'rgba(var(--acr),0.12)' : 'var(--sa)',
                color: showReplace ? 'var(--ac)' : 'var(--tm)',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              H
            </button>
            <button
              onClick={() => {
                setShowFind(false);
                setShowReplace(false);
                setFindText('');
              }}
              title="Close (Esc)"
              style={{
                padding: '3px 6px',
                borderRadius: 3,
                border: '1px solid var(--bd)',
                background: 'var(--sa)',
                color: 'var(--tm)',
                cursor: 'pointer',
                fontSize: 13,
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              &times;
            </button>
          </div>
          {showReplace && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 20 }}>
              <input
                placeholder="Replace with..."
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Escape') {
                    setShowFind(false);
                    setShowReplace(false);
                    setFindText('');
                  }
                }}
                style={{
                  flex: 1,
                  padding: '4px 8px',
                  fontSize: 13,
                  border: '1px solid var(--bd)',
                  borderRadius: 4,
                  background: 'var(--ib)',
                  color: 'var(--tx)',
                  outline: 'none',
                  minWidth: 0,
                }}
              />
              <button
                onClick={replaceOne}
                disabled={findMatches.length === 0}
                title="Replace"
                style={{
                  padding: '3px 10px',
                  borderRadius: 3,
                  border: '1px solid var(--bd)',
                  background: 'var(--sa)',
                  color: findMatches.length > 0 ? 'var(--tx)' : 'var(--tm)',
                  cursor: findMatches.length > 0 ? 'pointer' : 'default',
                  opacity: findMatches.length > 0 ? 1 : 0.3,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Replace
              </button>
              <button
                onClick={replaceAll}
                disabled={findMatches.length === 0}
                title="Replace all"
                style={{
                  padding: '3px 10px',
                  borderRadius: 3,
                  border: '1px solid var(--bd)',
                  background: 'var(--sa)',
                  color: findMatches.length > 0 ? 'var(--tx)' : 'var(--tm)',
                  cursor: findMatches.length > 0 ? 'pointer' : 'default',
                  opacity: findMatches.length > 0 ? 1 : 0.3,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                All
              </button>
            </div>
          )}
        </div>
      )}

      {/* Main content — document map + canvas + right panel */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Document Map (left panel) */}
        <div
          style={{
            width: docMapWidth,
            minWidth: docMapWidth,
            background: 'var(--sf)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '8px 10px',
              borderBottom: '1px solid var(--bd)',
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--tm)',
              textTransform: 'uppercase',
              letterSpacing: '.04em',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span>Document Map</span>
            <button
              title={collapsedMapIds.size > 0 ? 'Expand all' : 'Collapse all'}
              onClick={() => {
                const mapItems = docMapBlocks.filter((b: any) => b.level <= 3);
                const collapsibleIds = mapItems
                  .filter(
                    (b: any, mi: number) =>
                      mi < mapItems.length - 1 && mapItems[mi + 1].level > b.level
                  )
                  .map((b: any) => b.id);
                if (collapsedMapIds.size > 0) {
                  setCollapsedMapIds(new Set());
                } else {
                  setCollapsedMapIds(new Set(collapsibleIds));
                }
              }}
              style={{
                background: 'none',
                border: '1px solid var(--bd)',
                borderRadius: 3,
                cursor: 'pointer',
                color: 'var(--tm)',
                fontSize: 10,
                fontWeight: 700,
                width: 20,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                flexShrink: 0,
                transition: 'all .12s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--ac)';
                e.currentTarget.style.color = 'var(--ac)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--bd)';
                e.currentTarget.style.color = 'var(--tm)';
              }}
            >
              {collapsedMapIds.size > 0 ? '+' : '−'}
            </button>
          </div>
          <div style={{ padding: '4px 8px', borderBottom: '1px solid var(--bd)' }}>
            <input
              placeholder="Search map..."
              value={docMapSearch}
              onChange={(e) => setDocMapSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '3px 6px',
                fontSize: 10,
                border: '1px solid var(--bd)',
                borderRadius: 3,
                background: 'var(--ib)',
                color: 'var(--tx)',
                outline: 'none',
              }}
            />
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '4px 0' }}>
            {(() => {
              const allMapItems = docMapBlocks.filter((b: any) => b.level <= 4);
              // When searching, show all matching items (ignore collapse state); otherwise respect collapse
              const mapItems = docMapSearch.trim()
                ? allMapItems.filter((b: any) => {
                    const s = docMapSearch.toLowerCase();
                    return (
                      (b.text || '').toLowerCase().includes(s) ||
                      (b.prefix || '').toLowerCase().includes(s)
                    );
                  })
                : allMapItems;
              const isSearching = !!docMapSearch.trim();
              let hiddenUntilLevel: number | null = null;
              return mapItems.map((b: any, mi: number) => {
                // If we're hiding children under a collapsed parent, skip (not when searching)
                if (!isSearching && hiddenUntilLevel !== null) {
                  if (b.level > hiddenUntilLevel) return null;
                  hiddenUntilLevel = null;
                }
                // Check if this heading is collapsed (skip when searching)
                if (!isSearching && collapsedMapIds.has(b.id) && b.level <= 3) {
                  hiddenUntilLevel = b.level;
                }
                // Does this heading have children in the map?
                const hasChildren =
                  b.level <= 3 && mi < mapItems.length - 1 && mapItems[mi + 1].level > b.level;
                const isCollapsed = collapsedMapIds.has(b.id);
                const isVar = isBlockVariant(b.id);
                const isSel = b.id === selectedId;
                const indent = Math.max(0, b.level - 1) * 8;
                return (
                  <div key={b.id} style={{ display: 'flex', alignItems: 'center' }}>
                    {/* Expand/collapse toggle */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!hasChildren) return;
                        setCollapsedMapIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(b.id)) next.delete(b.id);
                          else next.add(b.id);
                          return next;
                        });
                      }}
                      style={{
                        width: 16,
                        minWidth: 16,
                        height: 16,
                        border: '1px solid',
                        borderRadius: 3,
                        padding: 0,
                        borderColor: hasChildren ? 'var(--bd)' : 'transparent',
                        background: hasChildren ? 'var(--sa)' : 'none',
                        cursor: hasChildren ? 'pointer' : 'default',
                        color: 'var(--tx)',
                        fontSize: 10,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: hasChildren ? 0.7 : 0,
                        marginLeft: 2,
                      }}
                      onMouseEnter={(e) => {
                        if (hasChildren) {
                          e.currentTarget.style.opacity = '1';
                          e.currentTarget.style.borderColor = 'var(--ac)';
                          e.currentTarget.style.color = 'var(--ac)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (hasChildren) {
                          e.currentTarget.style.opacity = '0.7';
                          e.currentTarget.style.borderColor = 'var(--bd)';
                          e.currentTarget.style.color = 'var(--tx)';
                        }
                      }}
                    >
                      {hasChildren ? (isCollapsed ? '+' : '−') : ''}
                    </button>
                    <div
                      onClick={() => {
                        setSelectedId(b.id);
                        setSelectedIds(new Set([b.id]));
                        scrollToBlock(b.id);
                      }}
                      style={{
                        flex: 1,
                        padding: `2px 8px 2px ${indent}px`,
                        fontSize: b.level === 1 ? 11 : 10,
                        fontWeight: b.level <= 2 ? 700 : 400,
                        color: isSel ? 'var(--ac)' : 'var(--tx)',
                        background: isSel
                          ? 'rgba(var(--acr),0.2)'
                          : showFind && findMatchBlockIds.has(b.id)
                            ? 'color-mix(in srgb, var(--color-warning) 15%, transparent)'
                            : 'transparent',
                        cursor: 'pointer',
                        borderLeft: '2px solid transparent',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        transition: 'background .1s',
                        lineHeight: 1.5,
                      }}
                      onMouseEnter={(e) => {
                        if (!isSel) e.currentTarget.style.background = 'var(--sa)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSel) e.currentTarget.style.background = 'transparent';
                      }}
                      title={b.text}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          color: 'var(--ac)',
                          opacity: 0.6,
                          marginRight: 3,
                          flexShrink: 0,
                        }}
                      >
                        {b.prefix}
                      </span>
                      {b.level <= 2 ? (b.text || '').toUpperCase() : b.text || ''}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Left panel resize handle */}
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startW = docMapWidth;
            function onMove(ev: MouseEvent) {
              const delta = ev.clientX - startX;
              setDocMapWidth(Math.max(140, Math.min(400, startW + delta)));
            }
            function onUp() {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
              document.body.style.cursor = '';
              document.body.style.userSelect = '';
            }
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
          }}
          style={{
            width: 4,
            flexShrink: 0,
            cursor: 'col-resize',
            background: 'var(--bd)',
            transition: 'background .15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--ac)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--bd)')}
        />

        {/* Document canvas */}
        <div
          ref={canvasColRef}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0,
            position: 'relative',
          }}
        >
          {/* View mode — Edit / Formatted (rendered document, forma applied) */}
          {!diffMode && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '7px 14px',
                borderBottom: '1px solid var(--bd)',
                background: 'var(--sf)',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--tm)',
                  flexShrink: 0,
                  order: 99,
                }}
              >
                {selectedIds.size > 1 && (
                  <span style={{ color: 'var(--ac)' }}>{selectedIds.size} selected</span>
                )}
                <span>Blocks: {blocks.length}</span>
              </span>
            </div>
          )}
          {/* Gutter-handle visibility: hidden until the row is hovered or
              selected. Class-based so no per-row hover state is needed. */}
          <style>{`
            .blk-row .blk-handle { opacity: 0; transition: opacity var(--duration-fast) var(--ease-out); }
            .blk-row:hover .blk-handle { opacity: 0.55; }
            .blk-row .blk-handle:hover { opacity: 1; background: var(--color-hover); }
            .blk-row .blk-handle:active { cursor: grabbing; }
            .blk-row .blk-prefix:hover { opacity: 1 !important; background: var(--color-hover); }
            .blk-row .blk-prefix:active { cursor: grabbing; }
            /* In-place text editing is chrome-free: the caret is the only change. */
            .blk-row textarea.blk-text-edit { caret-color: var(--ac); }
            .blk-row textarea.blk-text-edit:focus { outline: none; }
          `}</style>
          {/* Floating zoom — bottom-right of the canvas, same cluster as the
              Formas editor (one zoom UX app-wide). Hidden in diff view. */}
          {!diffMode && (
            <div style={{ position: 'absolute', right: 18, bottom: 14, zIndex: 20 }}>
              <ZoomCluster zoom={zoom} onZoomChange={setZoom} onFit={fitZoomToWindow} />
            </div>
          )}
          {/* Inline diff view replaces the editor when diffMode is on. */}
          {diffMode && <DiffView data={inlineDiffData} onExit={() => setDiffMode(false)} />}
          <div
            ref={canvasRef}
            onMouseDown={(e) => {
              // Click on the gray workspace margins (the scroll container itself, not
              // on a block) deselects everything. If the click lands on a block, the
              // block's own handler fires first and selection is preserved.
              if (e.target === e.currentTarget && !(e as any).defaultPrevented) {
                setSelectedId(null);
                setSelectedIds(new Set());
              }
            }}
            style={{
              flex: 1,
              overflow: 'auto',
              background: 'var(--sa)',
              display: diffMode ? 'none' : 'flex',
              padding: '24px 20px 40px',
            }}
          >
            <div
              onMouseDown={(e) => {
                // Clicks on the scaled wrapper itself (the vertical strip to the left/
                // right of the page, inside the zoom container) also count as "off the
                // page" and deselect.
                if (e.target === e.currentTarget) {
                  setSelectedId(null);
                  setSelectedIds(new Set());
                }
              }}
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: 'top center',
                margin: '0 auto',
              }}
            >
              {/* Page body */}
              <div
                style={{
                  width: pageW,
                  minHeight: 880,
                  background: 'var(--sf)',
                  boxShadow: 'var(--shadow-md)',
                  padding: '48px ' + pagePadH + 'px',
                  position: 'relative',
                  borderRadius: 2,
                }}
              >
                {/* Doc title */}
                <div
                  style={{
                    textAlign: 'center',
                    marginBottom: 24,
                    paddingBottom: 16,
                    borderBottom: '2px solid var(--bd)',
                  }}
                >
                  <div
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 14,
                      fontWeight: 700,
                      color: 'var(--ac)',
                      letterSpacing: '.05em',
                    }}
                  >
                    {`SECTION ${specCsi}`}
                  </div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      marginTop: 4,
                      textTransform: 'uppercase',
                      letterSpacing: '.03em',
                    }}
                  >
                    {specTitle}
                  </div>
                </div>

                {/* Blocks */}
                {numberedBlocks.map((block: any, idx: number) => {
                  const isSel = block.id === selectedId;
                  const isMultiSel = selectedIds.has(block.id);
                  const isEditing = editingBlockId === block.id;
                  // CSI Masterformat indent: L1 (PART) and L2 (1.01) both
                  // sit flush against the left margin (indent 0). L3+ stairs
                  // in by LEVEL_STEP per level, so L(n)'s number lands where
                  // L(n-1)'s text starts. Prefix slot is the same width as
                  // the step so spacing is identical across rows. Editor is
                  // only conveying structure; real type spacing comes from
                  // the forma at compile time.
                  const LEVEL_STEP = 36;
                  const indent = block.level <= 2 ? 0 : (block.level - 2) * LEVEL_STEP;
                  const style = levelStyle(block);
                  const displayText =
                    block.level <= 2 ? (block.text || '').toUpperCase() : block.text;
                  const isBlockDropTarget = blockDropIdx === idx && blockDragIdx !== idx;
                  const blockIsVariant = !!block.isVariant;
                  const isViewingVariant = activeVariantId && activeVariantId !== 'core';
                  const isCoreInVariantView = isViewingVariant && !blockIsVariant;
                  const excludedFromActive = !!(
                    isViewingVariant &&
                    !blockIsVariant &&
                    (block.excludedFromVariants || []).includes(activeVariantId!)
                  );
                  // View-option filters — hide the block entirely when one of
                  // the visibility toggles says it shouldn't appear. Defaults
                  // keep excluded-from-variant blocks visible (badged) so the
                  // user can see what's filtered out of the compiled output.
                  if (excludedFromActive && !viewOpts.showExcludedFromVariant) return null;
                  if ((block as any).excluded && !viewOpts.showExcluded) return null;
                  if (!activeVariantId && blockIsVariant && !viewOpts.showAllVariantBlocks) {
                    return null;
                  }
                  // Variant-view core visibility: hideCoreInVariant wins over
                  // dimCoreInVariant. If hidden, drop the row entirely.
                  if (isCoreInVariantView && viewOpts.hideCoreInVariant) return null;
                  const decorations: string[] = [];
                  if (block.underline) decorations.push('underline');
                  if (excludedFromActive) decorations.push('line-through');
                  const blockStyle: React.CSSProperties = {
                    ...style,
                    fontWeight: block.bold ? 700 : style.fontWeight,
                    fontStyle: block.italic ? 'italic' : undefined,
                    textDecoration: decorations.length ? decorations.join(' ') : undefined,
                  };

                  return (
                    <React.Fragment key={block.id}>
                      {/* HTML5 module-from-catalog drag was retired with the
                          modules feature in v2.12.0. Block reorder drag is
                          handled below via custom mouse events. */}
                      <div
                        id={'block-' + block.id}
                        data-block-idx={idx}
                        className="blk-row"
                        onMouseDown={(e) => {
                          // Drag now lives on the gutter handle. A plain
                          // mousedown on text only records the caret target for
                          // the click-to-type hand-off.
                          if (isEditing || e.button !== 0) return;
                          const tag = (e.target as HTMLElement).tagName;
                          if (
                            tag === 'BUTTON' ||
                            tag === 'INPUT' ||
                            tag === 'TEXTAREA' ||
                            (e.target as HTMLElement).closest('button')
                          )
                            return;
                          if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
                            captureCaretFromPoint(
                              e.currentTarget as HTMLElement,
                              e.clientX,
                              e.clientY
                            );
                          }
                        }}
                        onClick={(e) => {
                          setContextMenu(null);
                          handleBlockClick(block.id, e);
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setSelectedId(block.id);
                          setContextMenu({
                            x: e.clientX,
                            y: e.clientY,
                            blockId: block.id,
                            idx,
                          });
                        }}
                        style={{
                          display: 'flex',
                          gap: 0,
                          padding: '3px 4px 3px 0',
                          borderTop: isBlockDropTarget
                            ? '2px solid var(--ac)'
                            : '2px solid transparent',
                          borderRadius: 3,
                          cursor: 'text',
                          transition: 'background .1s, border-color .1s',
                          // Selection = a quiet accent bar on the left of the
                          // block (every block in a multi-selection gets one).
                          // No filled highlight box — the paper stays paper.
                          background:
                            (block as any).excluded || excludedFromActive
                              ? 'color-mix(in srgb, var(--color-danger) 5%, transparent)'
                              : 'transparent',
                          borderLeft:
                            (block as any).excluded || excludedFromActive
                              ? '3px solid var(--color-danger)'
                              : isSel || isMultiSel
                                ? '3px solid var(--ac)'
                                : '3px solid transparent',
                          position: 'relative',
                          opacity:
                            blockDragIdx === idx
                              ? 0.4
                              : isCoreInVariantView && viewOpts.dimCoreInVariant
                                ? 0.45
                                : (block as any).excluded
                                  ? 0.6
                                  : 1,
                          // Core blocks in a variant view are visually muted
                          // but remain interactive so the user can right-click
                          // to Exclude/Include from the active variant.
                          pointerEvents: 'auto',
                        }}
                        onMouseEnter={(e) => {
                          // Selection carries no background of its own (left
                          // bar only), so hover tint applies/clears on every
                          // row — no guard, no stuck tint after selecting.
                          if (blockDragIdx === null)
                            (e.currentTarget as HTMLElement).style.background = (block as any)
                              .excluded
                              ? 'color-mix(in srgb, var(--color-danger) 8%, transparent)'
                              : 'color-mix(in srgb, var(--color-text) 3%, transparent)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.background = (block as any)
                            .excluded
                            ? 'color-mix(in srgb, var(--color-danger) 5%, transparent)'
                            : 'transparent';
                        }}
                      >
                        {/* Gutter drag/select handle — appears on hover (CSS in
                            the canvas <style> block). Click = select the block
                            (structure work); drag = move it. Text stays
                            click-to-type. */}
                        {!isEditing && (
                          <div
                            className="blk-handle"
                            title="Click: select section · Alt+click: this block only · drag: move"
                            aria-label="Select or drag block"
                            onMouseDown={(e) => {
                              if (e.button !== 0) return;
                              e.preventDefault();
                              e.stopPropagation();
                              // Arming a drag on an unselected block selects it
                              // first — with its subtree (Alt = block only) —
                              // so the drag moves the whole branch as one.
                              if (!selectedIds.has(block.id)) {
                                const ids = e.altKey ? [block.id] : subtreeIdsOf(block.id);
                                setSelectedId(block.id);
                                setSelectedIds(new Set(ids));
                                setLastClickedId(block.id);
                                selectedIdsRef.current = new Set(ids);
                              }
                              setEditingBlockId(null);
                              blockDragRef.current = idx;
                              setBlockDragIdx(idx);
                              setBlockDragPos({ x: e.clientX, y: e.clientY });
                            }}
                            onClick={(e) => handleHandleClick(block.id, e)}
                            style={{
                              position: 'absolute',
                              left: -18,
                              top: 0,
                              bottom: 0,
                              width: 18,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'grab',
                              color: 'var(--tm)',
                              borderRadius: 'var(--radius-sm)',
                            }}
                          >
                            <svg
                              width="10"
                              height="14"
                              viewBox="0 0 10 16"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <circle cx="2.5" cy="2.5" r="1.4" />
                              <circle cx="7.5" cy="2.5" r="1.4" />
                              <circle cx="2.5" cy="8" r="1.4" />
                              <circle cx="7.5" cy="8" r="1.4" />
                              <circle cx="2.5" cy="13.5" r="1.4" />
                              <circle cx="7.5" cy="13.5" r="1.4" />
                            </svg>
                          </div>
                        )}
                        {/* Excluded badge — top-LEFT so it never fights with the floating action bar on the right. */}
                        {(block as any).excluded && (
                          <span
                            style={{
                              position: 'absolute',
                              top: -6,
                              left: 8,
                              background: 'var(--color-danger)',
                              color: 'var(--color-text-inverse)',
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: '.08em',
                              padding: '1px 6px',
                              borderRadius: 3,
                              textTransform: 'uppercase',
                              boxShadow: 'var(--shadow-sm)',
                              userSelect: 'none',
                              zIndex: 4,
                            }}
                            title="This block will be excluded from every variant"
                          >
                            Excluded
                          </span>
                        )}

                        {/* Block actions are in the right-click context menu — no floating toolbar */}

                        {/* Comment indicator — small badge when block has unresolved comments */}
                        {(block.comments || []).filter((c: any) => !c.resolved).length > 0 && (
                          <span
                            style={{
                              position: 'absolute',
                              top: 2,
                              right: 8,
                              fontSize: 10,
                              fontWeight: 700,
                              padding: '1px 5px',
                              borderRadius: 8,
                              background: 'var(--color-warning)',
                              color: 'var(--color-text-inverse)',
                              zIndex: 3,
                              cursor: 'default',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 3,
                            }}
                            title={`${(block.comments || []).filter((c: any) => !c.resolved).length} comment${(block.comments || []).filter((c: any) => !c.resolved).length !== 1 ? 's' : ''}`}
                          >
                            {(block.comments || []).filter((c: any) => !c.resolved).length}
                            <MessageSquare size={9} aria-hidden="true" />
                          </span>
                        )}

                        {/* Margin-side badge rail — masters only, All + Core
                            views. Absolute-positioned in the page's left
                            padding so the document content/prefix column is
                            never pushed sideways and All/Core/Variant views
                            all line up identically. The rail holds:
                              - V# / C pills for variant blocks (All view only)
                              - dashed strikethrough V# chips on core blocks
                                that are excluded from one or more variants
                                (All + Core views) — click to include.
                            Variant pills and exclusion chips are mutually
                            exclusive per row, so they share the rail. */}
                        {(!activeVariantId || activeVariantId === 'core') && (
                          <span
                            style={{
                              position: 'absolute',
                              top: 5,
                              right: '100%',
                              marginRight: 8,
                              display: 'inline-flex',
                              gap: 3,
                              flexShrink: 0,
                              userSelect: 'none',
                              pointerEvents: 'auto',
                            }}
                          >
                            {/* Variant pills — All view only */}
                            {!activeVariantId && viewOpts.showVariantPills && blockIsVariant && (
                              <span
                                style={{
                                  minWidth: 22,
                                  padding: '1px 4px',
                                  borderRadius: 3,
                                  background: 'var(--color-warning)',
                                  color: 'var(--color-text-inverse)',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  letterSpacing: '.02em',
                                  textAlign: 'center',
                                }}
                              >
                                {getVariantLabel(block)}
                              </span>
                            )}

                            {/* Excluded-from-variant chips — core blocks only,
                                  visible in All + Core. Click to include. */}
                            {!blockIsVariant &&
                              (block.excludedFromVariants || []).length > 0 &&
                              (block.excludedFromVariants as string[]).map((vid) => {
                                const vi = variants.findIndex((v) => v.id === vid);
                                if (vi < 0) return null;
                                return (
                                  <button
                                    key={vid}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleCoreExcludedFromVariant(block.id, vid);
                                    }}
                                    title={`Excluded from V${vi + 1}: ${variants[vi].name} — click to include`}
                                    style={{
                                      minWidth: 22,
                                      padding: '1px 4px',
                                      borderRadius: 3,
                                      border:
                                        '1px dashed color-mix(in srgb, var(--color-danger) 50%, transparent)',
                                      background: 'transparent',
                                      color:
                                        'color-mix(in srgb, var(--color-danger) 85%, transparent)',
                                      fontSize: 11,
                                      fontWeight: 600,
                                      letterSpacing: '.02em',
                                      textAlign: 'center',
                                      textDecoration: 'line-through',
                                      cursor: 'pointer',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    V{vi + 1}
                                  </button>
                                );
                              })}
                          </span>
                        )}

                        {/* EXCLUDED badge — core block that's been excluded
                            from the active variant. Loud visual so the user
                            can see at a glance which blocks won't ship when
                            this variant is compiled. */}
                        {excludedFromActive && (
                          <span
                            title={`This block is excluded from "${variants.find((v) => v.id === activeVariantId)?.name || 'this variant'}" and will NOT be included when compiled.`}
                            style={{
                              padding: '1px 6px',
                              borderRadius: 3,
                              background: 'var(--color-danger)',
                              color: 'var(--color-text-inverse)',
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: '.05em',
                              display: 'inline-flex',
                              alignItems: 'center',
                              flexShrink: 0,
                              marginTop: 3,
                              marginRight: 4,
                              userSelect: 'none',
                            }}
                          >
                            EXCLUDED
                          </span>
                        )}

                        {/* Prefix — continuation blocks show ↳ instead of a number.
                            The number is the SELECT affordance: click it to grab
                            the block (headings grab their whole subtree; Alt =
                            just this block). Clicking text types; clicking the
                            number selects. */}
                        <span
                          className="blk-prefix"
                          title="Click to select · Alt+click: this block only"
                          onMouseDown={(e) => {
                            // Behaves exactly like the gutter handle, including
                            // arming a drag — the number doubles as a grab zone.
                            if (e.button !== 0) return;
                            e.preventDefault();
                            e.stopPropagation();
                            if (!selectedIds.has(block.id)) {
                              const ids = e.altKey ? [block.id] : subtreeIdsOf(block.id);
                              setSelectedId(block.id);
                              setSelectedIds(new Set(ids));
                              setLastClickedId(block.id);
                              selectedIdsRef.current = new Set(ids);
                            }
                            setEditingBlockId(null);
                            blockDragRef.current = idx;
                            setBlockDragIdx(idx);
                            setBlockDragPos({ x: e.clientX, y: e.clientY });
                          }}
                          onClick={(e) => handleHandleClick(block.id, e)}
                          style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: block.level === 1 ? 13 : 11,
                            fontWeight: 700,
                            color: block.continuation ? 'var(--tm)' : 'var(--ac)',
                            opacity: block.continuation ? 0.4 : 0.7,
                            flexShrink: 0,
                            minWidth: LEVEL_STEP,
                            marginTop: 2,
                            marginLeft: indent,
                            userSelect: 'none',
                            cursor: 'grab',
                            borderRadius: 'var(--radius-sm)',
                          }}
                        >
                          {block.continuation
                            ? '↳'
                            : viewOpts.showParagraphNumbers
                              ? block.prefix
                              : ''}
                        </span>

                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {block.blockType === 'smart-token' ? (
                            <SmartTokenChip
                              block={block}
                              catalog={smartBlockCatalog}
                              selected={selectedId === block.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedId(block.id);
                              }}
                            />
                          ) : block.blockType === 'image' && block.imageData ? (
                            <ImageBlock
                              imageData={block.imageData}
                              text={block.text}
                              width={block.imageWidth}
                              isEditing={isEditing}
                              onResize={(w) => {
                                updateBlock(block.id, { imageWidth: w });
                                setDirty(true);
                              }}
                            />
                          ) : block.blockType === 'table' && block.tableData ? (
                            (() => {
                              const td = block.tableData;
                              const bw = td.borderWidth ?? 1;
                              const bc = '#' + (td.borderColor || '000000');
                              const bs = td.borderStyle || 'solid';
                              const ibw = td.innerBorderWidth ?? 0.5;
                              const ibc = '#' + (td.innerBorderColor || '999999');
                              const ibs = td.innerBorderStyle || 'solid';
                              return (
                                <div
                                  style={{ position: 'relative', margin: '4px 0' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <table
                                    style={{
                                      width: '100%',
                                      borderCollapse: 'collapse',
                                      border: `${bw}px ${bs} ${bc}`,
                                      tableLayout: 'fixed',
                                    }}
                                  >
                                    {td.colWidths && (
                                      <colgroup>
                                        {td.colWidths.map((w: number, ci: number) => (
                                          <col key={ci} style={{ width: w + '%' }} />
                                        ))}
                                      </colgroup>
                                    )}
                                    <tbody>
                                      {td.cells.map((row: any[], ri: number) => (
                                        <tr key={ri}>
                                          {row.map((cell: any, ci: number) => {
                                            const isHeader = ri < (td.headerRows || 0);
                                            const cellBg =
                                              isHeader && td.headerBgColor
                                                ? `#${td.headerBgColor}`
                                                : cell.bgColor
                                                  ? `#${cell.bgColor}`
                                                  : undefined;
                                            const cellOpacity =
                                              isHeader && td.headerBgOpacity != null
                                                ? td.headerBgOpacity / 100
                                                : cell.bgOpacity != null
                                                  ? cell.bgOpacity / 100
                                                  : 1;
                                            return (
                                              <td
                                                key={ci}
                                                colSpan={cell.colSpan || 1}
                                                rowSpan={cell.rowSpan || 1}
                                                onClick={() => {
                                                  if (!isEditing) {
                                                    // Remember WHICH cell was clicked so the
                                                    // edit-mode inputs focus it (not 0,0).
                                                    pendingCellRef.current = { r: ri, c: ci };
                                                    setEditingBlockId(block.id);
                                                  }
                                                }}
                                                style={{
                                                  border:
                                                    ibs === 'none'
                                                      ? 'none'
                                                      : `${ibw}px ${ibs} ${ibc}`,
                                                  padding: '3px 6px',
                                                  fontSize: 11,
                                                  lineHeight: 1.4,
                                                  fontWeight: isHeader || cell.bold ? 700 : 400,
                                                  fontStyle: cell.italic ? 'italic' : undefined,
                                                  textDecoration: cell.underline
                                                    ? 'underline'
                                                    : undefined,
                                                  textAlign: cell.align || 'left',
                                                  background: cellBg
                                                    ? cellBg +
                                                      (cellOpacity < 1
                                                        ? Math.round(cellOpacity * 255)
                                                            .toString(16)
                                                            .padStart(2, '0')
                                                        : '')
                                                    : undefined,
                                                  verticalAlign: 'top',
                                                  cursor: 'text',
                                                }}
                                              >
                                                {isEditing ? (
                                                  <input
                                                    autoFocus={
                                                      pendingCellRef.current
                                                        ? pendingCellRef.current.r === ri &&
                                                          pendingCellRef.current.c === ci
                                                        : ri === 0 && ci === 0
                                                    }
                                                    onFocus={() => {
                                                      pendingCellRef.current = null;
                                                    }}
                                                    data-cell-r={ri}
                                                    data-cell-c={ci}
                                                    value={cell.text}
                                                    onChange={(e) => {
                                                      const newCells = td.cells.map(
                                                        (r: any[], ri2: number) =>
                                                          r.map((c: any, ci2: number) =>
                                                            ri2 === ri && ci2 === ci
                                                              ? { ...c, text: e.target.value }
                                                              : c
                                                          )
                                                      );
                                                      updateBlock(block.id, {
                                                        tableData: { ...td, cells: newCells },
                                                      });
                                                    }}
                                                    onKeyDown={(e) => {
                                                      e.stopPropagation();
                                                      if (e.key === 'Tab') {
                                                        e.preventDefault();
                                                        // Navigate to next/prev cell
                                                        let nextR = ri,
                                                          nextC = ci + (e.shiftKey ? -1 : 1);
                                                        if (nextC >= td.cols) {
                                                          nextR++;
                                                          nextC = 0;
                                                        }
                                                        if (nextC < 0) {
                                                          nextR--;
                                                          nextC = td.cols - 1;
                                                        }
                                                        if (nextR >= 0 && nextR < td.rows) {
                                                          const nextInput = document.querySelector(
                                                            `[data-cell-r="${nextR}"][data-cell-c="${nextC}"]`
                                                          ) as HTMLInputElement;
                                                          if (nextInput) nextInput.focus();
                                                        }
                                                      }
                                                      if (e.key === 'Escape') {
                                                        setEditingBlockId(null);
                                                        e.preventDefault();
                                                      }
                                                    }}
                                                    style={{
                                                      width: '100%',
                                                      border: 'none',
                                                      background: 'transparent',
                                                      fontSize: 11,
                                                      padding: 0,
                                                      color: 'var(--tx)',
                                                      outline: 'none',
                                                      fontFamily: 'inherit',
                                                    }}
                                                  />
                                                ) : (
                                                  <span>{cell.text || '\u00A0'}</span>
                                                )}
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {/* Column resize handles */}
                                  {isEditing &&
                                    td.colWidths &&
                                    td.colWidths.slice(0, -1).map((_: number, ci: number) => {
                                      const leftPct = td
                                        .colWidths!.slice(0, ci + 1)
                                        .reduce((a: number, b: number) => a + b, 0);
                                      return (
                                        <div
                                          key={ci}
                                          onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const startX = e.clientX;
                                            const tableEl =
                                              e.currentTarget.parentElement?.querySelector('table');
                                            const tableW = tableEl?.offsetWidth || 400;
                                            const startWidths = [...td.colWidths!];
                                            function onMove(ev: MouseEvent) {
                                              const deltaPx = ev.clientX - startX;
                                              const deltaPct = (deltaPx / tableW) * 100;
                                              const nw = [...startWidths];
                                              nw[ci] = Math.max(5, startWidths[ci] + deltaPct);
                                              nw[ci + 1] = Math.max(
                                                5,
                                                startWidths[ci + 1] - deltaPct
                                              );
                                              updateBlock(block.id, {
                                                tableData: { ...td, colWidths: nw },
                                              });
                                            }
                                            function onUp() {
                                              document.removeEventListener('mousemove', onMove);
                                              document.removeEventListener('mouseup', onUp);
                                              document.body.style.cursor = '';
                                              document.body.style.userSelect = '';
                                            }
                                            document.addEventListener('mousemove', onMove);
                                            document.addEventListener('mouseup', onUp);
                                            document.body.style.cursor = 'col-resize';
                                            document.body.style.userSelect = 'none';
                                          }}
                                          style={{
                                            position: 'absolute',
                                            top: 0,
                                            bottom: 0,
                                            left: `${leftPct}%`,
                                            width: 6,
                                            transform: 'translateX(-3px)',
                                            cursor: 'col-resize',
                                            zIndex: 10,
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.background =
                                              'rgba(var(--acr),0.3)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                          }}
                                        />
                                      );
                                    })}
                                </div>
                              );
                            })()
                          ) : (
                            <>
                              {isEditing ? (
                                <textarea
                                  className="blk-text-edit"
                                  autoFocus
                                  value={block.text}
                                  onChange={(e) => updateBlock(block.id, { text: e.target.value })}
                                  onBlur={() => setEditingBlockId(null)}
                                  onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Escape') {
                                      // Bridge: leave the text, keep the block
                                      // selected for structure work.
                                      setEditingBlockId(null);
                                      setSelectedId(block.id);
                                      setSelectedIds(new Set([block.id]));
                                      e.preventDefault();
                                    }
                                    if (e.key === 'Tab') {
                                      e.preventDefault();
                                      changeLevel(idx, e.shiftKey ? -1 : 1);
                                    }
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      const ta = e.target as HTMLTextAreaElement;
                                      splitBlockAtCursor(idx, ta.selectionStart);
                                    }
                                    if (e.key === 'Backspace') {
                                      const ta = e.target as HTMLTextAreaElement;
                                      if (ta.selectionStart === 0 && ta.selectionEnd === 0) {
                                        e.preventDefault();
                                        mergeBlockIntoPrevious(idx);
                                      }
                                    }
                                  }}
                                  ref={(el) => {
                                    if (el) {
                                      // Auto-expand to the text's natural height
                                      // (no minimum — must match the display
                                      // span exactly so nothing jumps).
                                      el.style.height = 'auto';
                                      el.style.height = el.scrollHeight + 'px';
                                      // Caret hand-off from the click point on
                                      // the display span (null → caret at end).
                                      if (pendingCaretRef.current !== null) {
                                        const pos = Math.min(
                                          pendingCaretRef.current,
                                          el.value.length
                                        );
                                        pendingCaretRef.current = null;
                                        el.selectionStart = el.selectionEnd = pos;
                                      }
                                    }
                                  }}
                                  style={{
                                    // Chrome-free in-place editing: typography
                                    // inherits the block's own level style so
                                    // the swap from display span to editor is
                                    // invisible — the caret is the only change.
                                    // (Focus indication = the row's selection
                                    // outline; see .blk-text-edit CSS.)
                                    ...blockStyle,
                                    display: 'block',
                                    width: '100%',
                                    padding: 0,
                                    margin: 0,
                                    border: 'none',
                                    borderRadius: 0,
                                    background: 'transparent',
                                    color: 'var(--tx)',
                                    resize: 'none',
                                    overflow: 'hidden',
                                    whiteSpace: 'pre-wrap',
                                    wordWrap: 'break-word',
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span
                                  style={{
                                    ...blockStyle,
                                    wordWrap: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {!displayText ? (
                                    <span
                                      style={{
                                        color: 'var(--tm)',
                                        opacity: 0.4,
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      (empty)
                                    </span>
                                  ) : showFind && findText && findMatchBlockIds.has(block.id) ? (
                                    (() => {
                                      const parts: React.ReactNode[] = [];
                                      const text = displayText;
                                      const search = findMatchCase
                                        ? findText
                                        : findText.toLowerCase();
                                      const compare = findMatchCase ? text : text.toLowerCase();
                                      let pos = 0;
                                      while (pos < text.length) {
                                        const idx = compare.indexOf(search, pos);
                                        if (idx === -1) {
                                          parts.push(text.slice(pos));
                                          break;
                                        }
                                        if (idx > pos) parts.push(text.slice(pos, idx));
                                        const isCurrent = findCurrentBlockId === block.id;
                                        parts.push(
                                          <mark
                                            key={idx}
                                            style={{
                                              background: isCurrent
                                                ? 'var(--color-warning)'
                                                : 'color-mix(in srgb, var(--color-warning) 35%, transparent)',
                                              color: 'inherit',
                                              borderRadius: 2,
                                              padding: '0 1px',
                                            }}
                                          >
                                            {text.slice(idx, idx + findText.length)}
                                          </mark>
                                        );
                                        pos = idx + findText.length;
                                      }
                                      return <>{parts}</>;
                                    })()
                                  ) : (
                                    displayText
                                  )}
                                </span>
                              )}
                            </>
                          )}
                          {/* Module indicator — only show for variant modules, no end-of-block badge */}
                        </div>

                        {/* Level picker popup */}
                        {showLevelPicker && showLevelPicker.idx === idx && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: indent + 16,
                              zIndex: 100,
                              background: 'var(--sf)',
                              border: '1px solid var(--ac)',
                              borderRadius: 'var(--radius-md)',
                              padding: '6px 8px',
                              boxShadow: 'var(--shadow-lg)',
                              display: 'flex',
                              gap: 3,
                              alignItems: 'center',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                color: 'var(--tm)',
                                marginRight: 4,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Level:
                            </span>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((lvl) => (
                              <button
                                key={lvl}
                                onClick={() => moveBlockToLevel(showLevelPicker.fromIdx, idx, lvl)}
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 4,
                                  border: '1px solid var(--bd)',
                                  background:
                                    lvl === getCurrentBlocks()[showLevelPicker.fromIdx]?.level
                                      ? 'rgba(var(--acr),0.15)'
                                      : 'var(--sa)',
                                  color:
                                    lvl === getCurrentBlocks()[showLevelPicker.fromIdx]?.level
                                      ? 'var(--ac)'
                                      : 'var(--tx)',
                                  cursor: 'pointer',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                }}
                              >
                                {lvl}
                              </button>
                            ))}
                            <button
                              onClick={() => {
                                moveBlock(showLevelPicker.fromIdx, idx);
                                setShowLevelPicker(null);
                              }}
                              style={{
                                marginLeft: 4,
                                padding: '3px 8px',
                                borderRadius: 4,
                                border: '1px solid var(--ac)',
                                background: 'transparent',
                                color: 'var(--ac)',
                                cursor: 'pointer',
                                fontSize: 10,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Keep
                            </button>
                            <button
                              onClick={() => setShowLevelPicker(null)}
                              aria-label="Close level picker"
                              style={{
                                padding: '3px 6px',
                                borderRadius: 4,
                                border: '1px solid var(--bd)',
                                background: 'transparent',
                                color: 'var(--tm)',
                                cursor: 'pointer',
                                fontSize: 10,
                              }}
                            >
                              &times;
                            </button>
                          </div>
                        )}
                      </div>
                      {/* Variant insertion affordance — shown between ALL blocks in variant view */}
                      {isViewingVariant && (
                        <div
                          className="variant-insert-zone"
                          onClick={() => addBlockAfter(idx, true)}
                          style={{
                            height: 0,
                            position: 'relative',
                            cursor: 'pointer',
                            margin: '0 0 0 ' + indent + 'px',
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              top: -6,
                              height: 12,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0,
                              transition: 'opacity .15s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = '1';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = '0';
                            }}
                          >
                            <div style={{ flex: 1, height: 1, background: 'var(--ac)' }} />
                            <span
                              style={{
                                padding: '0 6px',
                                fontSize: 11,
                                fontWeight: 600,
                                color: 'var(--ac)',
                                whiteSpace: 'nowrap',
                                background: 'var(--sf)',
                                borderRadius: 3,
                              }}
                            >
                              + Insert
                            </span>
                            <div style={{ flex: 1, height: 1, background: 'var(--ac)' }} />
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Bottom drop zone (visual only — custom drag handles positioning) */}
                {isDragging && (
                  <div
                    data-block-idx={currentBlocks.length}
                    style={{
                      height: 32,
                      borderTop:
                        blockDropIdx === currentBlocks.length
                          ? '2px solid var(--ac)'
                          : '2px solid transparent',
                    }}
                  />
                )}

                <div style={{ padding: '12px 0' }}>
                  <button
                    onClick={() => addBlockAfter(currentBlocks.length - 1)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: 6,
                      border: '2px dashed var(--bd)',
                      background: 'transparent',
                      color: 'var(--tm)',
                      fontSize: 13,
                      cursor: 'pointer',
                      transition: 'all .15s',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--ac)';
                      (e.currentTarget as HTMLElement).style.color = 'var(--ac)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--bd)';
                      (e.currentTarget as HTMLElement).style.color = 'var(--tm)';
                    }}
                  >
                    + Add Block
                    {activeVariantId && activeVariantId !== 'core'
                      ? ` (V${variants.findIndex((v) => v.id === activeVariantId) + 1})`
                      : ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resize handle */}
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startW = rightPanelWidth;
            function onMove(ev: MouseEvent) {
              const delta = startX - ev.clientX;
              setRightPanelWidth(Math.max(200, Math.min(600, startW + delta)));
            }
            function onUp() {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
              document.body.style.cursor = '';
              document.body.style.userSelect = '';
            }
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
          }}
          style={{
            width: 4,
            flexShrink: 0,
            cursor: 'col-resize',
            background: 'var(--bd)',
            transition: 'background .15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--ac)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--bd)')}
        />

        {/* Right column — Properties Panel */}
        <div
          style={{
            width: rightPanelWidth,
            flexShrink: 0,
            overflow: 'hidden',
            background: 'var(--sf)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Right-panel tab strip — Block (selected block props) and
              Smart Blocks (palette of all available blocks for click-to-
              insert). Mouse-first per [[feedback-target-user-is-not-developer]];
              the Insert button in the main toolbar opens the same palette
              for users who prefer a button click over a tab switch. */}
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid var(--bd)',
              flexShrink: 0,
            }}
          >
            {(
              [
                { id: 'block', label: 'Block' },
                { id: 'smart-blocks', label: 'Smart Blocks' },
              ] as Array<{ id: 'block' | 'smart-blocks'; label: string }>
            ).map((t) => {
              const active = rightTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setRightTab(t.id)}
                  style={{
                    flex: 1,
                    padding: '8px 14px',
                    fontSize: 11,
                    fontWeight: 700,
                    color: active ? 'var(--ac)' : 'var(--tm)',
                    textTransform: 'uppercase',
                    letterSpacing: '.06em',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: active ? '2px solid var(--ac)' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'color .15s, border-color .15s',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          {rightTab === 'smart-blocks' ? (
            <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--tm)',
                  marginBottom: 10,
                  lineHeight: 1.5,
                }}
              >
                Click a block to insert it after the selected paragraph.
                {onOpenSmartBlocksLibrary && (
                  <>
                    {' '}
                    <span
                      onClick={onOpenSmartBlocksLibrary}
                      style={{
                        color: 'var(--ac)',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      Manage library →
                    </span>
                  </>
                )}
              </div>
              {(() => {
                // Merge built-in catalog with the firm's published smart blocks.
                // Built-ins go first so authors hit the headline kinds (Related
                // Sections, Project Identity, etc.) before scrolling to firm
                // customs.
                const all: SmartBlock[] = [...BUILT_IN_SMART_BLOCKS, ...smartBlockCatalog];
                return all.map((sb) => (
                  <button
                    key={sb.id}
                    onClick={() => {
                      const anchorId = selectedId || -1;
                      const defaults: Record<string, any> = {};
                      for (const p of sb.paramSchema || []) {
                        if (p.default !== undefined) defaults[p.key] = p.default;
                      }
                      openSmartTokenPicker(anchorId as any);
                      // Stash a pre-selection so the picker opens with this
                      // smart block highlighted. SmartTokenPicker reads
                      // this if available. (If not, the user still picks
                      // from inside the modal.)
                      try {
                        (window as any).__pendingSmartBlockId = sb.id;
                      } catch {}
                      // Direct-insert path: skip the modal entirely if the
                      // block has no required parameters. The chip becomes
                      // editable inline via the existing params panel.
                      const hasRequiredParams = (sb.paramSchema || []).some(
                        (p) => p.required && defaults[p.key] === undefined
                      );
                      if (!hasRequiredParams) {
                        setShowSmartTokenPicker(false);
                        const idx =
                          anchorId === -1
                            ? getCurrentBlocks().length - 1
                            : getCurrentBlocks().findIndex((b: any) => b.id === anchorId);
                        insertSmartToken(idx >= 0 ? idx : getCurrentBlocks().length - 1, {
                          smartBlockId: sb.id,
                          smartBlockKind: sb.kind,
                          smartParams: defaults,
                        });
                      }
                    }}
                    title={sb.description || sb.name}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '10px 12px',
                      marginBottom: 6,
                      background: 'var(--sf)',
                      border: '1px solid var(--bd)',
                      borderRadius: 8,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'border-color .15s, background .15s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--ac)';
                      e.currentTarget.style.background = 'var(--sa)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--bd)';
                      e.currentTarget.style.background = 'var(--sf)';
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        background: sb.color
                          ? sb.color + '20'
                          : 'color-mix(in srgb, var(--ac) 12%, transparent)',
                        color: sb.color || 'var(--ac)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      {sb.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'var(--tx)',
                          marginBottom: 2,
                        }}
                      >
                        {sb.name}
                      </div>
                      {sb.description && (
                        <div
                          style={{
                            fontSize: 10,
                            color: 'var(--tm)',
                            lineHeight: 1.4,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {sb.description}
                        </div>
                      )}
                    </div>
                  </button>
                ));
              })()}
            </div>
          ) : (
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              {!selected ? (
                <div style={{ padding: '16px 0' }}>
                  <div style={{ textAlign: 'center', color: 'var(--tm)', marginBottom: 16 }}>
                    <div style={{ fontSize: 11 }}>Click a block to see properties</div>
                  </div>
                  {/* Spec metadata — shown when no block is selected */}
                  {master && (
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: 'var(--tm)',
                          textTransform: 'uppercase',
                          letterSpacing: '.04em',
                          marginBottom: 8,
                        }}
                      >
                        Spec Info
                      </div>
                      {[
                        { key: 'owner', label: 'Owner', placeholder: 'Principal / EOR' },
                        { key: 'creator', label: 'Creator', placeholder: 'Who created this spec' },
                        { key: 'editor', label: 'Editor', placeholder: 'Who maintains this spec' },
                      ].map((f) => (
                        <div key={f.key} style={{ marginBottom: 8 }}>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: 'var(--tm)',
                              marginBottom: 2,
                            }}
                          >
                            {f.label}
                          </div>
                          <input
                            value={(master as any)[f.key] || ''}
                            placeholder={f.placeholder}
                            onChange={(e) => {
                              setMaster((prev: any) => ({ ...prev, [f.key]: e.target.value }));
                            }}
                            onBlur={async () => {
                              try {
                                await SpecService.savePublished(master);
                              } catch {}
                            }}
                            onKeyDown={(e) => e.stopPropagation()}
                            style={{
                              width: '100%',
                              padding: '4px 8px',
                              fontSize: 11,
                              border: '1px solid var(--bd)',
                              borderRadius: 4,
                              background: 'var(--ib)',
                              color: 'var(--tx)',
                            }}
                          />
                        </div>
                      ))}
                      <div style={{ marginBottom: 8 }}>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: 'var(--tm)',
                            marginBottom: 2,
                          }}
                        >
                          Notes
                        </div>
                        <textarea
                          value={(master as any).notes || ''}
                          placeholder="Internal notes..."
                          onChange={(e) => {
                            setMaster((prev: any) => ({ ...prev, notes: e.target.value }));
                          }}
                          onBlur={async () => {
                            try {
                              await SpecService.savePublished(master);
                            } catch {}
                          }}
                          onKeyDown={(e) => e.stopPropagation()}
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '4px 8px',
                            fontSize: 11,
                            border: '1px solid var(--bd)',
                            borderRadius: 4,
                            background: 'var(--ib)',
                            color: 'var(--tx)',
                            resize: 'vertical',
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {/* Block actions */}
                  <div style={{ marginBottom: 14 }}>
                    <div
                      style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 6 }}
                    >
                      Actions
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      <button
                        onClick={() => setEditingBlockId(selected.id)}
                        title="Edit text"
                        style={toolBtn}
                      >
                        <Pencil size={11} aria-hidden="true" /> Edit
                      </button>
                      <button
                        onClick={() => {
                          const idx = getCurrentBlocks().findIndex(
                            (b: any) => b.id === selected.id
                          );
                          if (idx >= 0) addBlockAfter(idx);
                        }}
                        title="Add block below"
                        style={toolBtn}
                      >
                        + Add Below
                      </button>
                      <button
                        onClick={() => setShowTablePicker(true)}
                        title="Insert table below"
                        style={toolBtn}
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="1" />
                          <line x1="3" y1="9" x2="21" y2="9" />
                          <line x1="9" y1="3" x2="9" y2="21" />
                        </svg>
                        Table
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.api?.openImageDialog) return;
                          const result = await window.api.openImageDialog();
                          if (!result) return;
                          const imgUri = result.data.startsWith('data:')
                            ? result.data
                            : `data:${result.mimeType || 'image/png'};base64,${result.data}`;
                          const idx = getCurrentBlocks().findIndex(
                            (b: any) => b.id === selected.id
                          );
                          if (idx < 0) return;
                          const newBlock = {
                            id:
                              'blk-' +
                              Date.now().toString(36) +
                              Math.random().toString(36).substr(2, 4),
                            level: selected.level,
                            text: result.name || '',
                            blockType: 'image' as const,
                            imageData: imgUri,
                          };
                          const updated = [...getCurrentBlocks()];
                          updated.splice(idx + 1, 0, newBlock);
                          setCurrentBlocks(updated);
                          setSelectedId(newBlock.id);
                          setDirty(true);
                          toast('Image inserted');
                        }}
                        title="Insert image below"
                        style={toolBtn}
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                        Image
                      </button>
                      <button
                        onClick={() => openSmartTokenPicker(selected.id)}
                        title="Insert smart block below"
                        style={toolBtn}
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M12 3l1.9 5.8L20 11l-5.8 1.9L12 19l-1.9-6.1L4 11l6.1-2.2z" />
                        </svg>
                        Smart
                      </button>
                      <button
                        onClick={() => {
                          const idx = getCurrentBlocks().findIndex(
                            (b: any) => b.id === selected.id
                          );
                          if (idx >= 0) duplicateBlock(idx);
                        }}
                        title="Duplicate"
                        style={toolBtn}
                      >
                        <CopyIcon size={11} aria-hidden="true" /> Dup
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => {
                            const idx = getCurrentBlocks().findIndex(
                              (b: any) => b.id === selected.id
                            );
                            if (idx >= 0) deleteBlock(idx);
                          }}
                          title="Delete"
                          aria-label="Delete block"
                          style={{ ...toolBtn, color: 'var(--dg)' }}
                        >
                          <Trash2 size={11} aria-hidden="true" /> Del
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <div
                      style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 4 }}
                    >
                      Level
                    </div>
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((lv) => (
                        <button
                          key={lv}
                          onClick={() => updateBlock(selected.id, { level: lv })}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 4,
                            border: '1px solid',
                            borderColor: selected.level === lv ? 'var(--ac)' : 'var(--bd)',
                            background:
                              selected.level === lv ? 'rgba(var(--acr),0.12)' : 'transparent',
                            color: selected.level === lv ? 'var(--ac)' : 'var(--tm)',
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {lv}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <div
                      style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 6 }}
                    >
                      Formatting
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        onClick={() => updateBlock(selected.id, { bold: !selected.bold })}
                        style={{
                          ...fmtBtn,
                          fontWeight: 700,
                          background: selected.bold ? 'rgba(var(--acr),0.15)' : 'var(--sa)',
                          color: selected.bold ? 'var(--ac)' : 'var(--tx)',
                          borderColor: selected.bold ? 'var(--ac)' : 'var(--bd)',
                        }}
                      >
                        B
                      </button>
                      <button
                        onClick={() => updateBlock(selected.id, { italic: !selected.italic })}
                        style={{
                          ...fmtBtn,
                          fontStyle: 'italic',
                          background: selected.italic ? 'rgba(var(--acr),0.15)' : 'var(--sa)',
                          color: selected.italic ? 'var(--ac)' : 'var(--tx)',
                          borderColor: selected.italic ? 'var(--ac)' : 'var(--bd)',
                        }}
                      >
                        I
                      </button>
                      <button
                        onClick={() => updateBlock(selected.id, { underline: !selected.underline })}
                        style={{
                          ...fmtBtn,
                          textDecoration: 'underline',
                          background: selected.underline ? 'rgba(var(--acr),0.15)' : 'var(--sa)',
                          color: selected.underline ? 'var(--ac)' : 'var(--tx)',
                          borderColor: selected.underline ? 'var(--ac)' : 'var(--bd)',
                        }}
                      >
                        U
                      </button>
                    </div>
                    {selected.level <= 2 && (
                      <div style={{ fontSize: 10, color: 'var(--tm)', marginTop: 4 }}>
                        Levels 1-2 display uppercase.
                      </div>
                    )}
                  </div>

                  {/* Smart-token params — inline when a smart-token block is selected. */}
                  {selected.blockType === 'smart-token' && (
                    <SmartTokenParamsPanel
                      block={selected}
                      catalog={smartBlockCatalog}
                      otherSpecsInProject={[]}
                      onChange={(newParams) => {
                        updateBlock(selected.id, { smartParams: newParams });
                        setDirty(true);
                      }}
                      onOpenCreator={onOpenSmartBlocksLibrary}
                    />
                  )}

                  {/* Table properties — inline when table block is selected */}
                  {selected.blockType === 'table' &&
                    selected.tableData &&
                    (() => {
                      const td = selected.tableData;
                      const updTable = (updates: any) =>
                        updateBlock(selected.id, { tableData: { ...td, ...updates } });
                      const propLabel: React.CSSProperties = {
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--tm)',
                        marginBottom: 4,
                      };
                      const propInput: React.CSSProperties = {
                        width: '100%',
                        padding: '5px 8px',
                        borderRadius: 5,
                        border: '1px solid var(--bd)',
                        background: 'var(--ib)',
                        color: 'var(--tx)',
                        fontSize: 13,
                        fontFamily: "'JetBrains Mono', monospace",
                      };

                      return (
                        <>
                          <div style={{ height: 1, background: 'var(--bd)', margin: '14px 0' }} />
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: 'var(--ac)',
                              marginBottom: 8,
                            }}
                          >
                            Table Properties
                          </div>
                          {/* Size */}
                          <div style={{ marginBottom: 14 }}>
                            <div style={propLabel}>Size</div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <div style={{ flex: 1 }}>
                                <span style={{ fontSize: 11, color: 'var(--tm)' }}>Rows</span>
                                <input
                                  type="number"
                                  min={1}
                                  max={50}
                                  value={td.rows}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    const newRows = Math.max(
                                      1,
                                      Math.min(50, parseInt(e.target.value) || 1)
                                    );
                                    const newCells = [...td.cells];
                                    while (newCells.length < newRows)
                                      newCells.push(
                                        Array(td.cols)
                                          .fill(null)
                                          .map(() => ({ text: '' }))
                                      );
                                    while (newCells.length > newRows) newCells.pop();
                                    updTable({ rows: newRows, cells: newCells });
                                  }}
                                  style={propInput}
                                />
                              </div>
                              <span style={{ fontSize: 13, color: 'var(--tm)', marginTop: 12 }}>
                                &times;
                              </span>
                              <div style={{ flex: 1 }}>
                                <span style={{ fontSize: 11, color: 'var(--tm)' }}>Cols</span>
                                <input
                                  type="number"
                                  min={1}
                                  max={20}
                                  value={td.cols}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    const newCols = Math.max(
                                      1,
                                      Math.min(20, parseInt(e.target.value) || 1)
                                    );
                                    const cw = Math.floor(100 / newCols);
                                    const colWidths = Array(newCols).fill(cw);
                                    colWidths[newCols - 1] = 100 - cw * (newCols - 1);
                                    const newCells = td.cells.map((row: any[]) => {
                                      const nr = [...row];
                                      while (nr.length < newCols) nr.push({ text: '' });
                                      while (nr.length > newCols) nr.pop();
                                      return nr;
                                    });
                                    updTable({ cols: newCols, cells: newCells, colWidths });
                                  }}
                                  style={propInput}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Justify */}
                          <div style={{ marginBottom: 14 }}>
                            <div style={propLabel}>Justification</div>
                            <div style={{ display: 'flex', gap: 3 }}>
                              {(['left', 'center', 'right'] as const).map((j) => (
                                <button
                                  key={j}
                                  onClick={() => updTable({ tableJustify: j })}
                                  style={{
                                    flex: 1,
                                    padding: '4px 0',
                                    borderRadius: 4,
                                    border: `1px solid ${td.tableJustify === j ? 'var(--ac)' : 'var(--bd)'}`,
                                    background:
                                      td.tableJustify === j
                                        ? 'rgba(var(--acr),0.12)'
                                        : 'transparent',
                                    color: td.tableJustify === j ? 'var(--ac)' : 'var(--tm)',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {j}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Header */}
                          <div style={{ marginBottom: 14 }}>
                            <div style={propLabel}>Header Rows</div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <input
                                type="number"
                                min={0}
                                max={td.rows}
                                value={td.headerRows || 0}
                                onKeyDown={(e) => e.stopPropagation()}
                                onChange={(e) =>
                                  updTable({
                                    headerRows: Math.max(
                                      0,
                                      Math.min(td.rows, parseInt(e.target.value) || 0)
                                    ),
                                  })
                                }
                                style={{ ...propInput, width: 60 }}
                              />
                              <input
                                type="color"
                                value={'#' + (td.headerBgColor || 'E8E8E8')}
                                onChange={(e) =>
                                  updTable({ headerBgColor: e.target.value.replace('#', '') })
                                }
                                style={{
                                  width: 28,
                                  height: 28,
                                  border: '1px solid var(--bd)',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  padding: 0,
                                }}
                              />
                            </div>
                          </div>

                          {/* Outer Border */}
                          <div style={{ marginBottom: 14 }}>
                            <div style={propLabel}>Outer Border</div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <input
                                type="number"
                                min={0}
                                max={6}
                                step={0.5}
                                value={td.borderWidth ?? 1}
                                onKeyDown={(e) => e.stopPropagation()}
                                onChange={(e) =>
                                  updTable({
                                    borderWidth: Math.max(0, parseFloat(e.target.value) || 0),
                                  })
                                }
                                style={{ ...propInput, width: 50 }}
                              />
                              <span style={{ fontSize: 11, color: 'var(--tm)' }}>pt</span>
                              <div style={{ minWidth: 110 }}>
                                <SearchableSelect
                                  value={td.borderStyle || 'solid'}
                                  options={[
                                    { value: 'solid', label: 'Solid' },
                                    { value: 'dashed', label: 'Dashed' },
                                    { value: 'dotted', label: 'Dotted' },
                                    { value: 'double', label: 'Double' },
                                  ]}
                                  onChange={(v) => updTable({ borderStyle: v })}
                                  clearable={false}
                                  searchPlaceholder="Search…"
                                  style={propInput}
                                />
                              </div>
                              <input
                                type="color"
                                value={'#' + (td.borderColor || '000000')}
                                onChange={(e) =>
                                  updTable({ borderColor: e.target.value.replace('#', '') })
                                }
                                style={{
                                  width: 28,
                                  height: 28,
                                  border: '1px solid var(--bd)',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  padding: 0,
                                }}
                              />
                            </div>
                          </div>

                          {/* Inner Border */}
                          <div style={{ marginBottom: 14 }}>
                            <div style={propLabel}>Inner Grid</div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <input
                                type="number"
                                min={0}
                                max={4}
                                step={0.25}
                                value={td.innerBorderWidth ?? 0.5}
                                onKeyDown={(e) => e.stopPropagation()}
                                onChange={(e) =>
                                  updTable({
                                    innerBorderWidth: Math.max(0, parseFloat(e.target.value) || 0),
                                  })
                                }
                                style={{ ...propInput, width: 50 }}
                              />
                              <span style={{ fontSize: 11, color: 'var(--tm)' }}>pt</span>
                              <div style={{ minWidth: 110 }}>
                                <SearchableSelect
                                  value={td.innerBorderStyle || 'solid'}
                                  options={[
                                    { value: 'solid', label: 'Solid' },
                                    { value: 'dashed', label: 'Dashed' },
                                    { value: 'dotted', label: 'Dotted' },
                                    { value: 'none', label: 'None' },
                                  ]}
                                  onChange={(v) => updTable({ innerBorderStyle: v })}
                                  clearable={false}
                                  searchPlaceholder="Search…"
                                  style={propInput}
                                />
                              </div>
                              <input
                                type="color"
                                value={'#' + (td.innerBorderColor || '999999')}
                                onChange={(e) =>
                                  updTable({ innerBorderColor: e.target.value.replace('#', '') })
                                }
                                style={{
                                  width: 28,
                                  height: 28,
                                  border: '1px solid var(--bd)',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  padding: 0,
                                }}
                              />
                            </div>
                          </div>

                          {/* Column Widths */}
                          {td.colWidths && (
                            <div style={{ marginBottom: 14 }}>
                              <div style={propLabel}>Column Widths (%)</div>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {td.colWidths.map((w: number, ci: number) => (
                                  <input
                                    key={ci}
                                    type="number"
                                    min={5}
                                    max={95}
                                    value={w}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const nw = [...td.colWidths!];
                                      nw[ci] = Math.max(
                                        5,
                                        Math.min(95, parseInt(e.target.value) || 10)
                                      );
                                      updTable({ colWidths: nw });
                                    }}
                                    style={{
                                      ...propInput,
                                      width: 48,
                                      textAlign: 'center' as const,
                                    }}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}

                  {/* Block comments */}
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--bd)' }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--tm)',
                        marginBottom: 6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      Comments
                      {(selected.comments || []).filter((c: any) => !c.resolved).length > 0 && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            padding: '1px 5px',
                            borderRadius: 8,
                            background: 'var(--color-warning)',
                            color: 'var(--color-text-inverse)',
                          }}
                        >
                          {(selected.comments || []).filter((c: any) => !c.resolved).length}
                        </span>
                      )}
                    </div>
                    {(selected.comments || []).map((c: any) => (
                      <div
                        key={c.id}
                        style={{
                          padding: '6px 8px',
                          marginBottom: 4,
                          borderRadius: 5,
                          background: c.resolved ? 'transparent' : 'var(--sa)',
                          border: '1px solid var(--bd)',
                          opacity: c.resolved ? 0.5 : 1,
                        }}
                      >
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}
                        >
                          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ac)' }}>
                            {c.author}
                          </span>
                          <span style={{ fontSize: 10, color: 'var(--tm)' }}>
                            {new Date(c.createdAt).toLocaleDateString()}
                          </span>
                          <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                            <button
                              onClick={() => {
                                updateBlock(selected.id, {
                                  comments: selected.comments.map((x: any) =>
                                    x.id === c.id ? { ...x, resolved: !x.resolved } : x
                                  ),
                                });
                                setDirty(true);
                              }}
                              title={c.resolved ? 'Unresolve' : 'Resolve'}
                              aria-label={c.resolved ? 'Unresolve comment' : 'Resolve comment'}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: c.resolved ? 'var(--ok)' : 'var(--tm)',
                                cursor: 'pointer',
                                fontSize: 11,
                                padding: '2px 4px',
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                            >
                              {c.resolved ? (
                                <Undo2 size={12} aria-hidden="true" />
                              ) : (
                                <Check size={12} aria-hidden="true" />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                updateBlock(selected.id, {
                                  comments: selected.comments.filter((x: any) => x.id !== c.id),
                                });
                                setDirty(true);
                              }}
                              title="Delete"
                              aria-label="Delete comment"
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--tm)',
                                cursor: 'pointer',
                                fontSize: 11,
                                padding: '2px 4px',
                                opacity: 0.5,
                              }}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--tx)', lineHeight: 1.4 }}>
                          {c.text}
                        </div>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                      <input
                        id="comment-input"
                        placeholder="Add a comment..."
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            const input = e.currentTarget;
                            if (!input.value.trim()) return;
                            const newComment = {
                              id:
                                'cmt-' +
                                Date.now().toString(36) +
                                Math.random().toString(36).slice(2, 5),
                              text: input.value.trim(),
                              author: userName,
                              createdAt: new Date().toISOString(),
                            };
                            updateBlock(selected.id, {
                              comments: [...(selected.comments || []), newComment],
                            });
                            setDirty(true);
                            input.value = '';
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: '4px 8px',
                          fontSize: 11,
                          border: '1px solid var(--bd)',
                          borderRadius: 4,
                          background: 'var(--ib)',
                          color: 'var(--tx)',
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Context menu */}
      {contextMenu &&
        (() => {
          const ctxBlock = blocks.find((b: any) => b.id === contextMenu.blockId);
          if (!ctxBlock) return null;
          const ctxIsVariant = !!ctxBlock.isVariant;
          const ctxIds = selectedIds.size > 1 ? [...selectedIds] : [contextMenu.blockId];
          const multi = ctxIds.length > 1;
          const anyIncluded = ctxIds.some(
            (id) => !(blocks.find((b: any) => b.id === id) as any)?.excluded
          );

          const activeVariant =
            activeVariantId && activeVariantId !== 'core'
              ? variants.find((v) => v.id === activeVariantId)
              : undefined;

          return (
            <BlockContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              ctxBlock={ctxBlock}
              ctxIds={ctxIds}
              ctxIsVariant={ctxIsVariant}
              variants={variants}
              canDelete={canDelete}
              anyIncluded={anyIncluded}
              multi={multi}
              idx={contextMenu.idx}
              activeVariant={activeVariant}
              onClose={() => setContextMenu(null)}
              onUpdateBlock={(id, updates) => {
                updateBlock(id, updates);
                setDirty(true);
              }}
              onEdit={() => {
                setEditingBlockId(contextMenu.blockId);
                setContextMenu(null);
              }}
              onDuplicate={() => {
                if (ctxIds.length > 1) duplicateBlocks(ctxIds);
                else duplicateBlock(contextMenu.idx);
                setContextMenu(null);
              }}
              onAddBelow={() => {
                addBlockAfter(contextMenu.idx);
                setContextMenu(null);
              }}
              onInsertImage={async () => {
                setContextMenu(null);
                if (!window.api?.openImageDialog) return;
                const result = await window.api.openImageDialog();
                if (!result) return;
                const imgUri = result.data.startsWith('data:')
                  ? result.data
                  : `data:${result.mimeType || 'image/png'};base64,${result.data}`;
                const newBlock: any = {
                  id: 'blk-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
                  level: blocks[contextMenu.idx]?.level || 3,
                  text: result.name || 'Image',
                  blockType: 'image',
                  imageData: imgUri,
                };
                const newBlocks = [...getCurrentBlocks()];
                newBlocks.splice(contextMenu.idx + 1, 0, newBlock);
                setCurrentBlocks(newBlocks);
                setSelectedId(newBlock.id);
                setDirty(true);
              }}
              onInsertSmartToken={() => {
                const anchorId = ctxBlock?.id ?? -1;
                openSmartTokenPicker(anchorId as any);
                setContextMenu(null);
              }}
              onToggleContinuation={() => {
                updateBlock(contextMenu.blockId, { continuation: !ctxBlock.continuation });
                setDirty(true);
                setContextMenu(null);
              }}
              onToggleCoreVariant={() => {
                // Whole selection lands in one state — direction from the
                // anchor block under the cursor.
                if (ctxIds.length > 1) toggleBlocksCoreVariant(ctxIds, !ctxBlock.isVariant);
                else toggleBlockCoreVariant(contextMenu.blockId);
                setContextMenu(null);
              }}
              onAssignToVariant={(variantId) => {
                if (ctxIds.length > 1) assignBlocksToVariant(ctxIds, variantId);
                else assignBlockToVariant(contextMenu.blockId, variantId);
              }}
              onToggleExcludeFromVariant={() => {
                if (activeVariant) {
                  ctxIds.forEach((id) => toggleCoreExcludedFromVariant(id, activeVariant.id));
                }
                setContextMenu(null);
              }}
              onToggleInclude={() => {
                setCurrentBlocks(
                  getCurrentBlocks().map((b: any) =>
                    ctxIds.includes(b.id) ? { ...b, excluded: anyIncluded } : b
                  )
                );
                setDirty(true);
                setContextMenu(null);
              }}
              onDelete={() => {
                deleteBlock(contextMenu.idx);
                setContextMenu(null);
              }}
            />
          );
        })()}

      {/* Keyboard shortcuts bar */}
      <div
        style={{
          padding: '3px 16px',
          borderTop: '1px solid var(--bd)',
          background: 'var(--sf)',
          display: 'flex',
          gap: 10,
          fontSize: 11,
          color: 'var(--tm)',
          flexWrap: 'wrap',
        }}
      >
        <span>
          <kbd style={kbdStyle}>Ctrl+Z</kbd> Undo
        </span>
        <span>
          <kbd style={kbdStyle}>Ctrl+Y</kbd> Redo
        </span>
        <span>
          <kbd style={kbdStyle}>Ctrl+C</kbd>/<kbd style={kbdStyle}>X</kbd>/
          <kbd style={kbdStyle}>V</kbd> Copy/Cut/Paste
        </span>
        <span>
          <kbd style={kbdStyle}>Tab</kbd> Indent
        </span>
        <span>
          <kbd style={kbdStyle}>Enter</kbd> Edit / split
        </span>
        <span>
          <kbd style={kbdStyle}>Esc</kbd> Select block
        </span>
        <span>
          <kbd style={kbdStyle}>Ctrl+D</kbd> Dup
        </span>
        <span>
          <kbd style={kbdStyle}>Ctrl+S</kbd> Save
        </span>
        <span>
          <kbd style={kbdStyle}>Shift+Click</kbd> Range
        </span>
        <span>
          <kbd style={kbdStyle}>Ctrl+Click</kbd> Multi
        </span>
        <span style={{ marginLeft: 'auto', fontWeight: 600, color: 'var(--tm)' }}>
          Click text to type · handle to select
        </span>
      </div>

      {/* ═══ MODALS ═══ */}

      {/* Insert Smart Block Picker */}
      {showSmartTokenPicker && (
        <SmartTokenPicker
          firmCatalog={smartBlockCatalog}
          onCancel={() => {
            setShowSmartTokenPicker(false);
            smartTokenInsertAnchorRef.current = null;
          }}
          onInsert={handleSmartTokenPickerInsert}
        />
      )}

      {/* Insert Table Picker */}
      {showTablePicker && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowTablePicker(false);
          }}
          style={overlayStyle}
        >
          <div style={{ ...modalStyle, width: 280 }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12 }}>Insert Table</div>
            <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 12 }}>
              Hover to select size, click to insert
            </div>
            <div
              style={{
                display: 'inline-grid',
                gridTemplateColumns: 'repeat(8, 24px)',
                gap: 2,
                marginBottom: 8,
              }}
            >
              {Array.from({ length: 64 }, (_, i) => {
                const r = Math.floor(i / 8) + 1,
                  c = (i % 8) + 1;
                const isHl = r <= tablePickerSize.r && c <= tablePickerSize.c;
                return (
                  <div
                    key={i}
                    onMouseEnter={() => setTablePickerSize({ r, c })}
                    onClick={() => {
                      const idx = selectedId
                        ? getCurrentBlocks().findIndex((b: any) => b.id === selectedId)
                        : getCurrentBlocks().length - 1;
                      insertTable(idx, tablePickerSize.r, tablePickerSize.c);
                      setShowTablePicker(false);
                      setRightTab('block');
                    }}
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 3,
                      border: '1px solid var(--bd)',
                      background: isHl ? 'rgba(var(--acr),0.2)' : 'var(--sa)',
                      cursor: 'pointer',
                      transition: 'all .08s',
                    }}
                  />
                );
              })}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ac)', textAlign: 'center' }}>
              {tablePickerSize.r > 0
                ? `${tablePickerSize.r} × ${tablePickerSize.c}`
                : 'Select size'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <Button variant="outline" size="sm" onClick={() => setShowTablePicker(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Save As */}
      {showSaveAsModal && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowSaveAsModal(false);
          }}
          style={overlayStyle}
        >
          <div style={modalStyle}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16 }}>
              Save As New Spec Master
            </div>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 4 }}>
                New Title
              </div>
              <input
                autoFocus
                value={saveAsTitle}
                onChange={(e) => setSaveAsTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveAs();
                  e.stopPropagation();
                }}
                style={{ ...modalInput, width: '100%' }}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="outline" size="sm" onClick={() => setShowSaveAsModal(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveAs}>
                Save As New
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Version History */}
      {showVersionHistory && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowVersionHistory(false);
          }}
          style={overlayStyle}
        >
          <div style={{ ...modalStyle, width: 520 }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>
              Version History (Published)
            </div>
            <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 16 }}>
              Published: v{master?.version || 1} — {(master?.versionHistory || []).length} archived
            </div>
            {!master?.versionHistory || master.versionHistory.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--tm)', fontSize: 13 }}>
                No previous versions yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[...(master.versionHistory || [])].reverse().map((v: any) => (
                  <div
                    key={v.version}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 6,
                      border: '1px solid var(--bd)',
                      background: 'var(--sa)',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        v{v.version}{' '}
                        <span style={{ fontWeight: 400, color: 'var(--tm)', fontSize: 11 }}>
                          by {v.savedBy} —{' '}
                          {new Date(v.savedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--tm)', marginTop: 2 }}>
                        {(v.snapshot.coreContent || []).length} blocks
                      </div>
                      {v.message && (
                        <div style={{ fontSize: 10, color: 'var(--tx)', fontStyle: 'italic' }}>
                          {v.message}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleRevert(v.version)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--ac)',
                        background: 'transparent',
                        color: 'var(--ac)',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Revert
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="outline" size="sm" onClick={() => setShowVersionHistory(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Submit for Review */}
      {showPublishModal && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowPublishModal(false);
          }}
          style={overlayStyle}
        >
          <div style={modalStyle}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>Submit for Review</div>
            <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 16 }}>
              An admin will review your changes before merging.
            </div>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 4 }}>
                Message (optional)
              </div>
              <textarea
                value={publishMessage}
                onChange={(e) => setPublishMessage(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Describe what changed..."
                rows={3}
                style={{ ...modalInput, width: '100%', resize: 'vertical' }}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="outline" size="sm" onClick={() => setShowPublishModal(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSubmitForReview}>
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Variant Modal */}
      {showVariantModal && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowVariantModal(false);
          }}
          style={overlayStyle}
        >
          <div
            style={modalStyle}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>Manage Variants</div>
            <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 16 }}>
              Variants let you create different configurations of this section (e.g., different
              manufacturers).
            </div>

            {variants.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                {variants.map((v) => {
                  const clientOrgs = organizations.filter(
                    (o) => o.type === 'client' || o.type === 'both'
                  );
                  return (
                    <div
                      key={v.id}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 6,
                        border: '1px solid var(--bd)',
                        background: 'var(--sa)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <input
                            value={v.name}
                            onChange={(e) => renameVariant(v.id, e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              border: 'none',
                              background: 'transparent',
                              color: 'var(--tx)',
                              padding: 0,
                              width: '100%',
                            }}
                          />
                        </div>
                        <button
                          onClick={() => deleteVariant(v.id)}
                          aria-label={`Delete variant ${v.name}`}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--bd)',
                            background: 'transparent',
                            color: 'var(--dg)',
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                      {/* Client association */}
                      {clientOrgs.length > 0 && (
                        <div
                          style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          <span style={{ fontSize: 10, color: 'var(--tm)', flexShrink: 0 }}>
                            Client:
                          </span>
                          <div style={{ flex: 1 }}>
                            <SearchableSelect
                              value={v.clientOrgId || ''}
                              options={clientOrgs.map((o) => ({ value: o.id, label: o.name }))}
                              onChange={(val) => {
                                setVariants((prev) =>
                                  prev.map((vr) =>
                                    vr.id === v.id ? { ...vr, clientOrgId: val || undefined } : vr
                                  )
                                );
                                setDirty(true);
                              }}
                              placeholder="— None —"
                              searchPlaceholder="Search clients…"
                              style={{ fontSize: 10, padding: '3px 6px' }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: 'flex', gap: 6 }}>
              <input
                autoFocus
                value={newVariantName}
                onChange={(e) => setNewVariantName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addVariant();
                  e.stopPropagation();
                }}
                placeholder="Variant name (e.g., Verkada, Axis...)"
                style={{ ...modalInput, flex: 1 }}
              />
              <Button size="sm" onClick={addVariant} disabled={!newVariantName.trim()}>
                Add
              </Button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="outline" size="sm" onClick={() => setShowVariantModal(false)}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Block drag ghost */}
      {blockDragIdx !== null &&
        blockDragPos &&
        (() => {
          const dragBlock = numberedBlocks[blockDragIdx];
          if (!dragBlock) return null;
          return (
            <div
              style={{
                position: 'fixed',
                left: blockDragPos.x + 16,
                top: blockDragPos.y - 8,
                padding: '4px 10px',
                borderRadius: 4,
                fontSize: 10,
                fontWeight: 600,
                background: 'var(--ac)',
                color: 'var(--color-text-inverse)',
                pointerEvents: 'none',
                zIndex: 9999,
                boxShadow: 'var(--shadow-lg)',
                whiteSpace: 'nowrap',
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {selectedIds.size > 1 && selectedIds.has(dragBlock.id)
                ? `${selectedIds.size} blocks`
                : `${dragBlock.prefix} ${dragBlock.text?.substring(0, 30) || '(empty)'}`}
            </div>
          );
        })()}

      {/* Old popup Diff Modal removed 2026-04-20 — replaced by the inline track-
        changes DiffView that takes over the canvas when diffMode is on (see
        handleShowDiff + the diffMode-gated render in the main canvas above). */}

      {/* Exit Guard Modal — forces save/discard/submit before leaving.
        Per Josh: this fires on EVERY exit attempt (not only when dirty) so
        the user must explicitly pick an outcome every time they leave a spec. */}

      {showExitGuard && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>
              {dirty ? 'Unsaved Changes' : 'Before You Leave'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--tm)', marginBottom: 20, lineHeight: 1.5 }}>
              {dirty ? (
                <>
                  You have unsaved changes to{' '}
                  <strong>
                    {specCsi} — {specTitle}
                  </strong>
                  . What would you like to do?
                </>
              ) : (
                <>
                  How would you like to leave{' '}
                  <strong>
                    {specCsi} — {specTitle}
                  </strong>
                  ?
                </>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={async () => {
                  await handleSave();
                  setShowExitGuard(false);
                  onBack();
                }}
                style={{
                  padding: '10px 16px',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: 'var(--ac)',
                  color: 'var(--color-text-inverse)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                Save Draft & Leave
                <div style={{ fontSize: 10, fontWeight: 400, opacity: 0.8, marginTop: 2 }}>
                  Save your changes as a draft
                </div>
              </button>
              <button
                onClick={async () => {
                  await handleSave();
                  setShowExitGuard(false);
                  if (canDirectPublish) {
                    await handlePublish();
                  } else {
                    setShowPublishModal(true);
                  }
                }}
                style={{
                  padding: '10px 16px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--ac)',
                  background: 'transparent',
                  color: 'var(--ac)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                {canDirectPublish ? 'Publish & Leave' : 'Submit for Review'}
                <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--tm)', marginTop: 2 }}>
                  {canDirectPublish
                    ? 'Publish changes immediately'
                    : 'Submit changes for admin approval'}
                </div>
              </button>
              <button
                onClick={() => {
                  setShowExitGuard(false);
                  if (draft) {
                    SpecService.discardDraft(draft.id).then(() => {
                      toast('Draft discarded');
                      onBack();
                    });
                  } else {
                    onBack();
                  }
                }}
                style={{
                  padding: '10px 16px',
                  borderRadius: 6,
                  border: '1px solid var(--dg)',
                  background: 'transparent',
                  color: 'var(--dg)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                Discard Changes & Leave
                <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--tm)', marginTop: 2 }}>
                  Discard all unsaved changes permanently
                </div>
              </button>
              <button
                onClick={() => {
                  setShowExitGuard(false);
                  if (onCancelExit) onCancelExit();
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: '1px solid var(--bd)',
                  background: 'transparent',
                  color: 'var(--tm)',
                  fontSize: 13,
                  cursor: 'pointer',
                  marginTop: 4,
                }}
              >
                Cancel — Keep Editing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── View Menu ───
// Popover rendered beneath the header's "View" button. Checkbox-style toggles
// live here (see ViewOpts) and persist to localStorage via the parent's
// useEffect. Track Changes is special-cased because it drives the diffMode
// render path rather than a plain render flag, so the parent passes a
// dedicated callback for that row.
interface ViewMenuProps {
  opts: ViewOpts;
  diffMode: boolean;
  onToggle: (key: keyof ViewOpts) => void;
  onToggleTrackChanges: () => void;
  onClose: () => void;
}

function ViewMenu({ opts, diffMode, onToggle, onToggleTrackChanges, onClose }: ViewMenuProps) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  // Click-outside / Escape closes the popover. Attached on the document so it
  // fires even when the user clicks another header button.
  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={rootRef}
      role="menu"
      aria-label="Display options"
      style={{
        position: 'absolute',
        top: 'calc(100% + 6px)',
        right: 0,
        zIndex: 100,
        minWidth: 260,
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)',
        padding: 'var(--space-1) 0',
        fontFamily: 'var(--font-sans)',
        animation: 'fade-in var(--duration-fast) var(--ease-out)',
      }}
    >
      <VMSectionLabel>Visibility</VMSectionLabel>
      <VMCheckItem
        label="Show excluded blocks"
        sub="Blocks flagged as excluded from export"
        checked={opts.showExcluded}
        onToggle={() => onToggle('showExcluded')}
      />
      <VMCheckItem
        label="Show variant-excluded core"
        sub="Core blocks marked EXCLUDED for the active variant. When off, they're hidden — matches compiled output."
        checked={opts.showExcludedFromVariant}
        onToggle={() => onToggle('showExcludedFromVariant')}
      />
      <VMCheckItem
        label="Show all variant blocks"
        sub={'In "All" view, include every variant\'s blocks'}
        checked={opts.showAllVariantBlocks}
        onToggle={() => onToggle('showAllVariantBlocks')}
      />
      <VMCheckItem
        label="Dim core in variant view"
        sub="When viewing a variant, mute core blocks so variant content dominates. Off = core looks normal (review the variant as a complete doc)."
        checked={opts.dimCoreInVariant}
        onToggle={() => onToggle('dimCoreInVariant')}
      />
      <VMCheckItem
        label="Hide core in variant view"
        sub="Show only the variant's own blocks. Overrides the dim toggle above."
        checked={opts.hideCoreInVariant}
        onToggle={() => onToggle('hideCoreInVariant')}
      />

      <VMDivider />
      <VMSectionLabel>Display</VMSectionLabel>
      <VMCheckItem
        label="Variant pills in gutter"
        sub='Colored "V1" / "Core" tag next to each block'
        checked={opts.showVariantPills}
        onToggle={() => onToggle('showVariantPills')}
      />
      <VMCheckItem
        label="Paragraph numbers"
        checked={opts.showParagraphNumbers}
        onToggle={() => onToggle('showParagraphNumbers')}
      />
      <VMDivider />
      <VMSectionLabel>Review</VMSectionLabel>
      <VMCheckItem
        label="Track changes"
        sub="Show differences vs the published master, Word-style"
        checked={diffMode}
        onToggle={onToggleTrackChanges}
      />
    </div>
  );
}

function VMCheckItem({
  label,
  sub,
  checked,
  onToggle,
  disabled,
}: {
  label: string;
  sub?: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      role="menuitemcheckbox"
      aria-checked={checked}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        border: 'none',
        background: 'transparent',
        color: disabled ? 'var(--tm)' : 'var(--tx)',
        fontSize: 13,
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        width: '100%',
        transition: 'background var(--duration-fast) var(--ease-out)',
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (!disabled) (e.currentTarget as HTMLElement).style.background = 'var(--color-hover)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 16,
          height: 16,
          marginTop: 1,
          border: `1.5px solid ${checked ? 'var(--ac)' : 'var(--bd)'}`,
          background: checked ? 'var(--ac)' : 'transparent',
          borderRadius: 'var(--radius-sm)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-inverse)',
          transition: 'all var(--duration-fast) var(--ease-out)',
        }}
      >
        {checked && <Check size={11} strokeWidth={3} />}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: checked ? 600 : 400 }}>{label}</div>
        {sub && (
          <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2, lineHeight: 1.4 }}>
            {sub}
          </div>
        )}
      </span>
    </button>
  );
}

function VMSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 'var(--space-2) var(--space-3) 2px',
        fontSize: 10,
        fontWeight: 600,
        color: 'var(--tm)',
        textTransform: 'uppercase',
        letterSpacing: '.08em',
        userSelect: 'none',
      }}
    >
      {children}
    </div>
  );
}

function VMDivider() {
  return (
    <div style={{ height: 1, background: 'var(--bd)', margin: 'var(--space-1) var(--space-2)' }} />
  );
}

// ─── Block Context Menu ───
// Word-style right-click menu. Real viewport clamping via measured rect,
// a quick-action toolbar at top, and hover-triggered submenus that flip
// left when they would overflow the right edge of the screen.

interface BlockContextMenuProps {
  x: number;
  y: number;
  ctxBlock: any;
  ctxIds: string[];
  ctxIsVariant: boolean;
  variants: Variant[];
  canDelete: boolean;
  anyIncluded: boolean;
  multi: boolean;
  idx: number;
  /** The variant currently active in the editor, if any. Used to render the
   *  "Exclude from [variant name]" item on core blocks, which lets the user
   *  hide a core block from one variant without deleting it from core. */
  activeVariant?: Variant;
  onClose: () => void;
  onUpdateBlock: (id: string, updates: any) => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onAddBelow: () => void;
  onInsertImage: () => void;
  onInsertSmartToken: () => void;
  onToggleContinuation: () => void;
  onToggleCoreVariant: () => void;
  onAssignToVariant: (variantId: string) => void;
  onToggleExcludeFromVariant: () => void;
  onToggleInclude: () => void;
  onDelete: () => void;
}

const CM_MIN_W = 244;
const CM_MAX_W = 320;
const SUBMENU_W = 180;

function BlockContextMenu(props: BlockContextMenuProps) {
  const {
    x,
    y,
    ctxBlock,
    ctxIds,
    ctxIsVariant,
    variants,
    canDelete,
    anyIncluded,
    multi,
    idx,
    activeVariant,
    onClose,
    onUpdateBlock,
    onEdit,
    onDuplicate,
    onAddBelow,
    onInsertImage,
    onInsertSmartToken,
    onToggleContinuation,
    onToggleCoreVariant,
    onAssignToVariant,
    onToggleExcludeFromVariant,
    onToggleInclude,
    onDelete,
  } = props;

  const menuRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ left: number; top: number }>({ left: x, top: y });
  const [openSub, setOpenSub] = React.useState<null | 'level' | 'variants'>(null);
  const [subAnchor, setSubAnchor] = React.useState<{ top: number; flipLeft: boolean }>({
    top: 0,
    flipLeft: false,
  });
  const subTimer = React.useRef<number | null>(null);

  // Clamp menu within viewport using the actual rendered size.
  React.useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pad = 8;
    let left = x;
    let top = y;
    if (left + rect.width > vw - pad) left = Math.max(pad, vw - rect.width - pad);
    if (top + rect.height > vh - pad) top = Math.max(pad, vh - rect.height - pad);
    if (left !== pos.left || top !== pos.top) setPos({ left, top });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x, y, openSub]);

  // Close on Escape.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const scheduleOpen = (which: 'level' | 'variants', row: HTMLElement) => {
    if (subTimer.current) window.clearTimeout(subTimer.current);
    const rect = row.getBoundingClientRect();
    const menuRect = menuRef.current?.getBoundingClientRect();
    const right = (menuRect?.right ?? rect.right) + SUBMENU_W + 8;
    setSubAnchor({
      top: rect.top - (menuRect?.top ?? 0),
      flipLeft: right > window.innerWidth,
    });
    setOpenSub(which);
  };
  const scheduleClose = () => {
    if (subTimer.current) window.clearTimeout(subTimer.current);
    subTimer.current = window.setTimeout(() => setOpenSub(null), 180);
  };
  const cancelClose = () => {
    if (subTimer.current) window.clearTimeout(subTimer.current);
  };

  const applyUpdate = (updates: any) => {
    for (const id of ctxIds) onUpdateBlock(id, updates);
  };

  const headerLabel =
    ctxBlock.blockType === 'table'
      ? 'Table block'
      : ctxBlock.blockType === 'image'
        ? 'Image block'
        : `Level ${ctxBlock.level} block`;
  const baseLabel = 'Variants';

  // Reflect the right-clicked block's state — toggling applies to all selected ids.
  const showBold = !!ctxBlock.bold;
  const showItalic = !!ctxBlock.italic;
  const showUnderline = !!ctxBlock.underline;

  return (
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
      }}
    >
      <div
        ref={menuRef}
        onMouseDown={(e) => e.stopPropagation()}
        role="menu"
        aria-label="Block actions"
        style={{
          position: 'fixed',
          left: pos.left,
          top: pos.top,
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-lg)',
          padding: 'var(--space-1) 0 var(--space-2)',
          minWidth: CM_MIN_W,
          maxWidth: CM_MAX_W,
          zIndex: 2001,
          fontFamily: 'var(--font-sans)',
          animation: 'fade-in var(--duration-fast) var(--ease-out)',
        }}
      >
        {/* Quick-action toolbar */}
        {ctxBlock.blockType !== 'image' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              padding: 'var(--space-1) var(--space-2)',
              borderBottom: '1px solid var(--bd)',
              marginBottom: 'var(--space-1)',
            }}
          >
            <CMToolBtn
              active={showBold}
              title="Bold"
              onClick={() => applyUpdate({ bold: !showBold })}
            >
              <Bold size={14} />
            </CMToolBtn>
            <CMToolBtn
              active={showItalic}
              title="Italic"
              onClick={() => applyUpdate({ italic: !showItalic })}
            >
              <Italic size={14} />
            </CMToolBtn>
            <CMToolBtn
              active={showUnderline}
              title="Underline"
              onClick={() => applyUpdate({ underline: !showUnderline })}
            >
              <Underline size={14} />
            </CMToolBtn>
            <div style={{ width: 1, height: 18, background: 'var(--bd)', margin: '0 4px' }} />
            <CMToolBtn
              title="Decrease level"
              disabled={ctxBlock.level <= 1}
              onClick={() => applyUpdate({ level: Math.max(1, (ctxBlock.level || 1) - 1) })}
            >
              <ChevronUp size={14} />
            </CMToolBtn>
            <CMToolBtn
              title="Increase level"
              disabled={ctxBlock.level >= 9}
              onClick={() => applyUpdate({ level: Math.min(9, (ctxBlock.level || 1) + 1) })}
            >
              <ChevronDown size={14} />
            </CMToolBtn>
            <div style={{ flex: 1 }} />
            <CMToolBtn title="Duplicate" onClick={onDuplicate}>
              <CopyIcon size={14} />
            </CMToolBtn>
            {canDelete && (
              <CMToolBtn title="Delete" destructive onClick={onDelete}>
                <Trash2 size={14} />
              </CMToolBtn>
            )}
          </div>
        )}

        {/* Header strip */}
        <div
          style={{
            padding: '2px 14px 6px',
            fontSize: 10,
            color: 'var(--tm)',
            userSelect: 'none',
            borderBottom: '1px solid var(--bd)',
            marginBottom: 4,
          }}
        >
          {multi ? `${ctxIds.length} blocks selected` : headerLabel}
        </div>

        <CMItem icon={<Pencil size={14} />} label="Edit block" shortcut="Enter" onClick={onEdit} />
        <CMItem
          icon={<CopyIcon size={14} />}
          label={multi ? `Duplicate (${ctxIds.length})` : 'Duplicate'}
          shortcut="Ctrl+D"
          onClick={onDuplicate}
        />

        <CMDivider />

        {/* INSERT */}
        <CMSectionLabel>Insert</CMSectionLabel>
        <CMItem icon={<Plus size={14} />} label="Add block below" onClick={onAddBelow} />
        <CMItem icon={<ImagePlus size={14} />} label="Insert image below" onClick={onInsertImage} />
        <CMItem
          icon={
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 3l1.9 5.8L20 11l-5.8 1.9L12 19l-1.9-6.1L4 11l6.1-2.2z" />
            </svg>
          }
          label="Insert smart block below"
          onClick={onInsertSmartToken}
        />
        {/* "Insert module below" retired in v2.12.0 — modules feature removed. */}

        {/* FORMAT */}
        <CMDivider />
        <CMSectionLabel>Format</CMSectionLabel>
        {idx > 0 && (
          <CMItem
            icon={<CornerDownRight size={14} />}
            label="Continuation (no number)"
            checked={!!ctxBlock.continuation}
            title="Share the previous block's paragraph number — renders without its own prefix."
            onClick={onToggleContinuation}
          />
        )}
        <CMSubRow
          icon={<Hash size={14} />}
          label="Change level"
          onEnter={(row) => scheduleOpen('level', row)}
          onLeave={scheduleClose}
          active={openSub === 'level'}
        />

        {/* VARIANTS */}
        <CMDivider />
        <CMSectionLabel>Variants</CMSectionLabel>
        <CMItem
          icon={<GitBranch size={14} />}
          label={ctxIsVariant ? 'Mark as core' : 'Mark as variant'}
          onClick={onToggleCoreVariant}
        />
        {ctxIsVariant && variants.length > 0 && (
          <CMSubRow
            icon={<ChevronRight size={14} />}
            label="Assign to variant"
            onEnter={(row) => scheduleOpen('variants', row)}
            onLeave={scheduleClose}
            active={openSub === 'variants'}
          />
        )}
        {/* Core-in-variant exclusion: visible only when the user is inside
            a variant view and right-clicks a core block. Toggles whether
            this core block renders (and exports) in the active variant. */}
        {!ctxIsVariant && activeVariant && (
          <CMItem
            icon={<EyeOff size={14} />}
            label={(() => {
              const excluded = (ctxBlock.excludedFromVariants || []).includes(activeVariant.id);
              return excluded
                ? `Include in "${activeVariant.name}"`
                : `Exclude from "${activeVariant.name}"`;
            })()}
            title="Hide this core block from the active variant without deleting it from core."
            onClick={onToggleExcludeFromVariant}
          />
        )}

        <CMDivider />
        <CMSectionLabel>Organization</CMSectionLabel>
        <CMItem
          icon={anyIncluded ? <EyeOff size={14} /> : <Eye size={14} />}
          label={
            anyIncluded
              ? `Exclude from ${baseLabel}${multi ? ` (${ctxIds.length})` : ''}`
              : `Include in ${baseLabel}${multi ? ` (${ctxIds.length})` : ''}`
          }
          title="Block stays in the editor but is skipped at print/export time."
          onClick={onToggleInclude}
        />
        {/* "Create module" + "Remove from module" retired in v2.12.0. */}

        {canDelete && (
          <>
            <CMDivider />
            <CMItem
              icon={<Trash2 size={14} />}
              label={multi ? `Delete (${ctxIds.length} blocks)` : 'Delete block'}
              shortcut="Del"
              destructive
              onClick={onDelete}
            />
          </>
        )}

        {/* Submenus — absolutely positioned siblings of the items so hover state
            can straddle the gap without closing. */}
        {openSub === 'level' && (
          <div
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            role="menu"
            aria-label="Change level"
            style={{
              position: 'absolute',
              top: subAnchor.top,
              left: subAnchor.flipLeft ? undefined : '100%',
              right: subAnchor.flipLeft ? '100%' : undefined,
              marginLeft: subAnchor.flipLeft ? 0 : 4,
              marginRight: subAnchor.flipLeft ? 4 : 0,
              background: 'var(--sf)',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-lg)',
              padding: 'var(--space-1) 0',
              minWidth: SUBMENU_W,
              zIndex: 2002,
              animation: 'fade-in var(--duration-fast) var(--ease-out)',
            }}
          >
            {[1, 2, 3, 4, 5].map((lvl) => (
              <CMItem
                key={lvl}
                icon={<Hash size={14} />}
                label={`Level ${lvl}`}
                checked={ctxBlock.level === lvl}
                onClick={() => {
                  applyUpdate({ level: lvl });
                  setOpenSub(null);
                  onClose();
                }}
              />
            ))}
          </div>
        )}
        {openSub === 'variants' && (
          <div
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            role="menu"
            aria-label="Assign to variant"
            style={{
              position: 'absolute',
              top: subAnchor.top,
              left: subAnchor.flipLeft ? undefined : '100%',
              right: subAnchor.flipLeft ? '100%' : undefined,
              marginLeft: subAnchor.flipLeft ? 0 : 4,
              marginRight: subAnchor.flipLeft ? 4 : 0,
              background: 'var(--sf)',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-lg)',
              padding: 'var(--space-1) 0',
              minWidth: SUBMENU_W,
              maxHeight: 280,
              overflowY: 'auto',
              zIndex: 2002,
              animation: 'fade-in var(--duration-fast) var(--ease-out)',
            }}
          >
            {variants.map((v) => {
              const inVariant = ctxBlock.variantId === v.id;
              return (
                <CMItem
                  key={v.id}
                  icon={<GitBranch size={14} />}
                  label={v.name}
                  checked={inVariant}
                  onClick={() => {
                    onAssignToVariant(v.id);
                    setOpenSub(null);
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CMToolBtn({
  children,
  onClick,
  title,
  active,
  destructive,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  active?: boolean;
  destructive?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        border: 'none',
        background: active ? 'rgba(var(--acr),0.15)' : 'transparent',
        color: disabled
          ? 'var(--tm)'
          : destructive
            ? 'var(--dg)'
            : active
              ? 'var(--ac)'
              : 'var(--tx)',
        borderRadius: 'var(--radius-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background var(--duration-fast) var(--ease-out)',
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        (e.currentTarget as HTMLElement).style.background = destructive
          ? 'color-mix(in srgb, var(--dg) 8%, transparent)'
          : active
            ? 'rgba(var(--acr),0.22)'
            : 'var(--color-hover)';
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        (e.currentTarget as HTMLElement).style.background = active
          ? 'rgba(var(--acr),0.15)'
          : 'transparent';
      }}
    >
      {children}
    </button>
  );
}

function CMItem({
  icon,
  label,
  shortcut,
  onClick,
  destructive,
  checked,
  title,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
  shortcut?: string;
  onClick: () => void;
  destructive?: boolean;
  checked?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      role="menuitem"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2)',
        border: 'none',
        background: 'transparent',
        color: destructive ? 'var(--dg)' : checked ? 'var(--ac)' : 'var(--tx)',
        fontSize: 13,
        fontWeight: checked ? 600 : 400,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background var(--duration-fast) var(--ease-out)',
        borderRadius: 'var(--radius-sm)',
        margin: '0 4px',
        width: 'calc(100% - 8px)',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = destructive
          ? 'color-mix(in srgb, var(--dg) 8%, transparent)'
          : 'var(--color-hover)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      <span
        style={{
          width: 16,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: destructive ? 1 : 0.7,
          flexShrink: 0,
        }}
      >
        {checked ? <Check size={14} strokeWidth={2.5} /> : icon}
      </span>
      <span
        style={{
          flex: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </span>
      {shortcut && (
        <span
          style={{
            color: 'var(--tm)',
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            marginLeft: 'var(--space-3)',
            opacity: 0.8,
            flexShrink: 0,
          }}
        >
          {shortcut}
        </span>
      )}
    </button>
  );
}

function CMSubRow({
  icon,
  label,
  onEnter,
  onLeave,
  active,
}: {
  icon: React.ReactNode;
  label: string;
  onEnter: (row: HTMLElement) => void;
  onLeave: () => void;
  active: boolean;
}) {
  return (
    <button
      onMouseEnter={(e) => onEnter(e.currentTarget)}
      onMouseLeave={onLeave}
      onClick={(e) => onEnter(e.currentTarget)}
      role="menuitem"
      aria-haspopup="menu"
      aria-expanded={active}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2)',
        border: 'none',
        background: active ? 'var(--color-hover)' : 'transparent',
        color: 'var(--tx)',
        fontSize: 13,
        cursor: 'pointer',
        textAlign: 'left',
        borderRadius: 'var(--radius-sm)',
        margin: '0 4px',
        width: 'calc(100% - 8px)',
      }}
    >
      <span
        style={{
          width: 16,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.7,
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
      <ChevronRight size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
    </button>
  );
}

function CMSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 'var(--space-2) var(--space-3) 2px',
        fontSize: 10,
        fontWeight: 600,
        color: 'var(--tm)',
        textTransform: 'uppercase',
        letterSpacing: '.08em',
        userSelect: 'none',
      }}
    >
      {children}
    </div>
  );
}

function CMDivider() {
  return (
    <div style={{ height: 1, background: 'var(--bd)', margin: 'var(--space-1) var(--space-2)' }} />
  );
}

// ─── Inline track-changes diff view ───
// Replaces the editor canvas when the user toggles "Show Diff". Renders blocks
// in document order, styled per diff status:
//   added     — green background + green left border
//   removed   — red background + red left border + strikethrough text
//   modified  — amber background + word-level additions (green) and deletions
//                (red strikethrough) inline
//   unchanged — subtle / muted
// Read-only. Click "Exit diff view" (or the Diff button in the header) to return.
function DiffView({ data, onExit }: { data: InlineDiffBlock[]; onExit: () => void }) {
  const summary = React.useMemo(() => {
    let added = 0;
    let removed = 0;
    let modified = 0;
    let unchanged = 0;
    for (const d of data) {
      if (d.kind === 'added') added++;
      else if (d.kind === 'removed') removed++;
      else if (d.kind === 'modified') modified++;
      else unchanged++;
    }
    return { added, removed, modified, unchanged };
  }, [data]);

  const noChanges = summary.added === 0 && summary.removed === 0 && summary.modified === 0;

  return (
    <div
      style={{
        flex: 1,
        overflow: 'auto',
        background: 'var(--sa)',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 20px 40px',
      }}
    >
      {/* Sticky summary bar */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 8,
          padding: '10px 14px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          fontSize: 13,
          boxShadow: 'var(--shadow-sm)',
          zIndex: 10,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 13 }}>Track Changes</span>
        {noChanges ? (
          <span style={{ color: 'var(--tm)' }}>No changes between draft and published.</span>
        ) : (
          <>
            <DiffPill color="var(--color-success)" label={`${summary.added} added`} />
            <DiffPill color="var(--color-danger)" label={`${summary.removed} removed`} />
            <DiffPill color="var(--color-warning)" label={`${summary.modified} modified`} />
            <span style={{ color: 'var(--tm)', fontSize: 11 }}>
              + {summary.unchanged} unchanged
            </span>
          </>
        )}
        <button
          onClick={onExit}
          style={{
            marginLeft: 'auto',
            padding: '5px 12px',
            borderRadius: 5,
            border: '1px solid var(--bd)',
            background: 'var(--sa)',
            color: 'var(--tx)',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Exit diff view
        </button>
      </div>

      {/* Blocks, styled per diff kind */}
      <div
        style={{
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 8,
          padding: '12px 0',
          width: 680,
          maxWidth: '100%',
          alignSelf: 'center',
        }}
      >
        {data.map((d, i) => (
          <DiffRow key={`${d.id}-${i}`} d={d} />
        ))}
      </div>
    </div>
  );
}

function DiffPill({ color, label }: { color: string; label: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        fontWeight: 600,
        color,
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
      {label}
    </span>
  );
}

function DiffRow({ d }: { d: InlineDiffBlock }) {
  const indent = Math.max(0, (d.level - 1) * 20);
  const bg =
    d.kind === 'added'
      ? 'color-mix(in srgb, var(--color-success) 10%, transparent)'
      : d.kind === 'removed'
        ? 'color-mix(in srgb, var(--color-danger) 10%, transparent)'
        : d.kind === 'modified'
          ? 'color-mix(in srgb, var(--color-warning) 10%, transparent)'
          : 'transparent';
  const border =
    d.kind === 'added'
      ? 'var(--color-success)'
      : d.kind === 'removed'
        ? 'var(--color-danger)'
        : d.kind === 'modified'
          ? 'var(--color-warning)'
          : 'transparent';

  return (
    <div
      style={{
        display: 'flex',
        padding: '6px 14px 6px 0',
        background: bg,
        borderLeft: `3px solid ${border}`,
        marginBottom: 2,
      }}
    >
      <span
        style={{
          width: 24,
          textAlign: 'center',
          fontSize: 11,
          fontWeight: 700,
          color: border === 'transparent' ? 'var(--tm)' : border,
          userSelect: 'none',
          paddingTop: 2,
        }}
      >
        {d.kind === 'added' ? '+' : d.kind === 'removed' ? '−' : d.kind === 'modified' ? '≈' : ' '}
      </span>
      <div style={{ paddingLeft: indent, flex: 1, fontSize: 13, lineHeight: 1.5 }}>
        {d.blockType === 'image' ? (
          <span style={{ color: 'var(--tm)', fontStyle: 'italic' }}>
            [image]
            {d.kind === 'removed' || d.kind === 'modified' ? ' previously shown' : ''}
          </span>
        ) : d.blockType === 'table' ? (
          <span style={{ color: 'var(--tm)', fontStyle: 'italic' }}>[table]</span>
        ) : d.kind === 'modified' && d.wordDiff ? (
          <WordDiffText tokens={d.wordDiff} />
        ) : d.kind === 'removed' ? (
          <span style={{ color: 'var(--color-danger)', textDecoration: 'line-through' }}>
            {d.oldText}
          </span>
        ) : d.kind === 'added' ? (
          <span style={{ color: 'var(--color-success)' }}>{d.newText}</span>
        ) : (
          <span style={{ color: 'var(--tm)' }}>{d.newText}</span>
        )}
      </div>
    </div>
  );
}

function WordDiffText({ tokens }: { tokens: DiffToken[] }) {
  return (
    <span>
      {tokens.map((t, i) => {
        if (t.op === 'add') {
          return (
            <span
              key={i}
              style={{
                background: 'color-mix(in srgb, var(--color-success) 18%, transparent)',
                color: 'var(--color-success)',
                borderRadius: 2,
                padding: '0 1px',
              }}
            >
              {t.value}
            </span>
          );
        }
        if (t.op === 'del') {
          return (
            <span
              key={i}
              style={{
                color: 'var(--color-danger)',
                textDecoration: 'line-through',
                background: 'color-mix(in srgb, var(--color-danger) 12%, transparent)',
                borderRadius: 2,
                padding: '0 1px',
              }}
            >
              {t.value}
            </span>
          );
        }
        return <span key={i}>{t.value}</span>;
      })}
    </span>
  );
}

// ─── Shared styles ───
// (headerBtn removed — header toolbar now composes the kit Button primitive.)
const toolBtn: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--bd)',
  background: 'var(--sa)',
  color: 'var(--tm)',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
};
const fmtBtn: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--bd)',
  cursor: 'pointer',
  fontSize: 13,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const kbdStyle: React.CSSProperties = {
  padding: '1px 4px',
  borderRadius: 2,
  background: 'var(--sa)',
  border: '1px solid var(--bd)',
  fontSize: 11,
};
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(0,0,0,.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: 'var(--sf)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  width: 420,
  border: '1px solid var(--bd)',
  boxShadow: 'var(--shadow-xl)',
  maxHeight: '80vh',
  overflow: 'auto',
};
const modalInput: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 13,
};
const ctxMenuBtnBase: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '6px 14px',
  border: 'none',
  background: 'transparent',
  color: 'var(--tx)',
  fontSize: 11,
  cursor: 'pointer',
  textAlign: 'left',
};

(window as any).BlockEditor = BlockEditor;
export { BlockEditor };
